/**
 * The production PixiJS mount for COMBAT.app's map surface.
 * `CombatStage.tsx` owns a host `<div>` and calls {@link mountCombatBoard} once from a `useEffect`;
 * this module builds the `Application` + world-root container and exposes a small imperative handle
 * (`set*` sync functions + `destroy()`) that `CombatStage.tsx` drives whenever the relevant React state
 * changes. Deliberately a thin imperative mount, NOT `@pixi/react`.
 *
 * It draws: the world-root pan/zoom transform, the board background (bezel/ruler), the terrain
 * art and edge walls (chunk textures from `terrainArt/terrainLayer.ts`), the PLAY full grid, rooms
 * (render-only), the authoring-preview ghosts (tile box/hover/stroke, wall
 * run, room run), tokens + name labels, the breathing selection halo, the
 * move-range/move-path/remote-move-path/attack-overlay washes, persisted draw annotations + the
 * in-progress draw preview, and remote cursors/ping bursts.
 *
 * **The interaction model is native-DOM + pure math, NOT Pixi's own `hitArea`/`eventMode`
 * interactivity.** Token click-select/drag hit-testing is footprint-AABB math (`combatCells.ts`'s
 * `hitTestToken`) driven from `CombatStage.tsx`'s native pointer handlers, and the
 * dblclick-to-inspect room hit-test is point-in-rect against the `rooms` prop. Token drag is "the
 * renderer moves it live, state commits on drop": `CombatStage.tsx` calls {@link
 * CombatBoardHandle.moveToken} per `mousemove`, and the halo/labels ticker below reads a token's LIVE
 * container position directly off this module's own `tokenEntries` map (deliberately not through
 * `usePresentationDirector`'s `NodeRegistry` — that registry exists for the animator's tweens, a
 * separate concern from the ticker's per-frame label/halo follow).
 *
 * Every `build*` function returns a fresh `Container` from scratch; every `set*` handle method clears
 * and rebuilds its container's children. Pixi has no bake-to-bitmap layer-cache trick to port — a
 * retained GPU scene graph doesn't need one, so there is no
 * re-cache bookkeeping to port. **`setTokens` is the one deliberate exception:** it
 * reconciles by combatant id instead of destroying/rebuilding every token container on every call — a
 * persisting id's container/label are restyled in place (and only repositioned when presentation's own
 * `pos` genuinely changed), and only added/removed ids create/destroy a container. See
 * {@link TokenEntry}'s doc for the full "why" (a parity regression: `setTokens`' old destroy-everything
 * behavior raced `pixiAnimator.ts`'s in-flight tweens/drags and snapped a forced/remote move's slide).
 *
 * Pixi has no built-in dashed-stroke support — {@link dashedSegment}/{@link dashedRect} synthesize one
 * by chopping a path into alternating draw/skip sub-segments before a single `.stroke()` call. It also
 * has no built-in curve-tension smoothing for a line — {@link drawSmoothLine} approximates one via
 * quadratic-through-midpoints.
 */
import { Application, BlurFilter, Container, Graphics, ImageSource, RendererType, Sprite, type StrokeInput, Text, TextStyle, Texture, UPDATE_PRIORITY } from "pixi.js";
import type { Annotation, AnnotationColor, AnnotationId, AreaEffect, AreaEffectId, Combatant, EdgeMap, MovePathPreview, Room, RoomId, SitrepZone, EncounterGround, TerrainCell, TerrainMap } from "@lancer-vtt/domain";
import { clearancePercent, type ZoneClearance } from "@lancer-vtt/rules";
import { controlsCombatant, hasStatus, HIDDEN_STATUS, isOpenDoor, roomBoundaryEdges, roomFloorCells, roomVolume } from "@lancer-vtt/domain";
import { squareSpace } from "@lancer-vtt/space";
import type { DroneAuraRing } from "../../lib/encounter/droneAuras";
import { CELL, SCENERY_TOKEN_LOOK, asSquareCell, footprintBox, parseTileKey, tokenLook } from "./combatCells";
import { areaEffectStyle, ARMED_COVER_HATCH, type AreaEffectStyle, type HatchStyle } from "./terrainPalette";
import { storageGet, storageSet } from "../../lib/browserStorage";
import { columnLabel } from "../../lib/encounter/cellLabel";
import type { CellSightline } from "../../lib/encounter/combatInspector";
import type { ThreatRingsView } from "../../lib/encounter/hoverIntel";
import type { FloatSpec, FloatTone } from "../../lib/encounter/presentationDirector";
import { solveLabelLayout, type LabelItem, type PlacedWhere, type Rect } from "./labelLayout";
import { placeTargetBadges } from "./targetBadgeLayout";
import { tokenImageFor, type TokenImage, type TokenImageCtx } from "../../lib/tokenArt";
import { drawTokenSprite, type TokenArtState } from "./pixiTokenSprite";
import { STATE_COLOR, activeBeaconId, activeBeaconRadius, drawTokenChrome, drawTokenChromeUnder, tokenChromeObstacle } from "./pixiTokenChrome";
import { prefersReducedMotion } from "../../lib/prefersReducedMotion";
import { hexPoints, insideLabelFloor, markRingRadius, tokenHex, tokenOverflow } from "./hexGeometry";
import { tokenChromeKey, type TokenChrome } from "../../lib/encounter/tokenChrome";
import { applyTextResolution, boardResolution, createViewSettled, textLevel, textResolution, type SettledView } from "./boardResolution";
import { bandGeometry, cellCentres, dashLoop } from "./overlayBands";
import { chunkDensity } from "./terrainArt/chunks";
import { type SwatchSpec, swatchKey, tileSwatches } from "./terrainArt/swatches";
import { type TerrainBuildStats, createTerrainLayer, screenWorldRect } from "./terrainArt/terrainLayer";
import { createAssetLayer } from "./terrainArt/assetLayer";
import { isDrawnAsAsset, placedAssets, tokenLabelText } from "./terrainArt/assetLook";
import { ZONE_KIND_LABEL } from "./sitrepZoneLabel";
import { createAttackFxLooks, type AttackFxLogEntry } from "./attackFx/attackFxLooks";
import { FxScene } from "./attackFx/fxScene";
import type { AttackOutcomeItem, AttackTravelItem } from "../../lib/encounter/attackFxItems";
import { MECHANICS_LABEL_FONT, mechanicsLabelText, piecesContaining, terrainMechanicsPieces, type TerrainMechanicsPiece } from "./terrainMechanics";

// ── Renderer build counter ─────────────────────────────────────────────────────────────────────
// A module-level counter incremented once per {@link mountCombatBoard} call — i.e. once per
// `new Application()`, once per real Pixi renderer/WebGL-context/shader build. `CombatStage.tsx`
// populates `window.__combatBoardDebug` at mount and **`delete`s it on unmount**, so a counter
// living only inside that object would be USELESS for proving "the board did not rebuild": if the
// board unmounts and rebuilds (exactly what a flow needs to detect), the fresh mount republishes a
// fresh debug object and a flow reading through it would see whatever the NEW mount's own field
// says, not a value that survived the swap. This counter is module state instead — it lives for
// the whole page session, outlives every mount/unmount of `CombatStage`, and is published on its
// OWN persistent `window` key below (never deleted), so an e2e flow can read it BEFORE a socket
// drop and AFTER the reconnect and assert equality: unchanged ⇒ the renderer was never rebuilt,
// incremented ⇒ it was.
let combatBoardBuildCount = 0;

/** The number of times {@link mountCombatBoard} has built a real Pixi `Application` this page
 *  session — see the block comment above for why this is module state, not part of the
 *  mount-scoped `window.__combatBoardDebug`. Also mirrored onto `__combatBoardDebug.getBuildCount`
 *  (convenient while the board is mounted) — this export/its `window.__combatBoardBuildCount`
 *  twin is the load-bearing one because it survives the unmount that convenience object doesn't. */
export function getCombatBoardBuildCount(): number {
  return combatBoardBuildCount;
}

declare global {
  // Same sanctioned `interface` (declaration merging) exception `CombatStage.tsx`'s
  // `__combatBoardDebug` augmentation takes.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    /** TEST-ONLY — the persistent twin of {@link getCombatBoardBuildCount}, published once at
     *  module load (NOT inside `CombatStage.tsx`'s mount effect), so it is never deleted by that
     *  component's unmount cleanup. See the `combatBoardBuildCount` block comment above. */
    __combatBoardBuildCount?: () => number;
  }
}
if (typeof window !== "undefined") {
  window.__combatBoardBuildCount = getCombatBoardBuildCount;
}

// ── Whole-board frame-rate cap ──────────────────────────────────────────────────────────────────
// Pixi's `Ticker.maxFPS` is a first-class field. A
// high-refresh monitor would otherwise redraw the whole board at the display rate for identical
// motion; capping is a ceiling, not a floor, so motion stays frame-rate-independent (every animation
// integrates elapsed time, never a frame count).
const DEFAULT_MAX_FPS = 60;
const MAX_FPS_STORAGE_KEY = "lvtt.maxFps";

function readInitialMaxFps(): number {
  // An unreachable store or an unset key both fall back to the default.
  const stored = storageGet(MAX_FPS_STORAGE_KEY);
  if (stored.ok && stored.value !== undefined) {
    const n = Number(stored.value);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return DEFAULT_MAX_FPS;
}

function persistMaxFps(fps: number): void {
  // Non-fatal on failure — the in-memory cap still applies for this session.
  storageSet(MAX_FPS_STORAGE_KEY, String(fps));
}

// ── Board chrome palette: the GMS//OS brutalist sheet (`brutalist-tokens.css`) ──────────────────
/** The stage around the board: `--brutalist-stage`, the shell's empty stage. */
const STAGE_BG = 0x111010;
/** The board's fill until the terrain layer's first paint lands (the atlas is loading). */
const BOARD_PLACEHOLDER = "#2a2820";
/** The PLAY-mode full grid (ruling 2): faint lines over the terrain, a view toggle. */
const FULL_GRID = { color: "#f4ecce", alpha: 0.16, width: 1 } as const;
/** The board's frame: one crisp bone rule on a hard ink shadow, no glow. */
const FRAME_INK = "#e9e6df";
const FRAME_SHADOW = { offset: 6, color: "#000000", alpha: 0.45 } as const;
/** The coordinate ruler's muted bone ink (`director.css`'s `--dir-ash`). */
const RULER_INK = "#8d8a83";
const RULER_FONT = 9;
const RULER_FONT_FAMILY = "JetBrains Mono, monospace";

const ROOM_FLOOR_FILL = "rgba(95, 227, 255, 0.09)";
const ROOM_FLOOR_BORDER = "rgba(150, 236, 255, 0.85)";
const ROOM_FLOOR_GLOW = "#5fe3ff";
const ROOM_LABEL_INK = "rgba(190, 246, 255, 0.92)";
const ROOM_LABEL_SUB = "rgba(95, 227, 255, 0.78)";
const ROOM_LABEL_NAME_FONT = 12;
const ROOM_LABEL_SUB_FONT = 10;
const ROOM_LABEL_PAD = 5;
/** The dark outline under room and zone label text, so it reads over the textured ground. */
const LABEL_UNDER = { color: "#06080c", width: 3, join: "round" } as const;

/** **Sitrep zones** — the first browser surface for zones at all.
 *  Violet is the one hue this board's palette had left unclaimed (cyan = terrain/rooms/walls, pink =
 *  foe, amber = dangerous terrain, mint = tech/sensors), so an authored objective region can never be
 *  mistaken for a map feature the GM painted. Flat washes + a border, no new geometry
 *  (`visual-effects-need-human-loop` — ship simple over unvettable fancy). */
const ZONE_ACCENT = "#b38cff";
/** A zone is an overlay band (`terrain-tile-art` 5.1), kept quiet (developer, 2026-09-26: "deemphasize
 *  the designated zones"): a near-zero violet wash and a thin, half-alpha edge on a light under-stroke,
 *  so the zone frames the ground rather than competing with the reach bands; its label and progress
 *  line carry the read. A `hidden` zone's edge is dashed. */
const ZONE_BAND = { wash: "rgba(200, 180, 255, 0.02)", edge: "rgba(214, 200, 255, 0.5)", width: 1.25, underAlpha: 0.3 } as const;
const ZONE_LABEL_INK = "rgba(224, 209, 255, 0.95)";
/** Bright enough to stay legible ON TOP of {@link ZONE_TODO_FILL} — the progress line sits inside
 *  the zone, so it lands over the still-obstructed cells more often than not, and it is the number
 *  the table reads. A dimmer violet (the first cut) washed out against them. */
const ZONE_LABEL_SUB = "rgba(234, 226, 255, 0.92)";
/** A still-obstructed cell of a `clear` zone — the "where is there left to bulldoze" read, which is the
 *  whole reason this layer beats a bare percentage in the HUD. Brighter than the zone wash so the
 *  remaining work pops out of it, but light enough that the painted terrain under it still reads
 *  (developer, 2026-09-25: at 0.30 fill + 0.55 stroke the landing zone hid the terrain art), and
 *  quieter again with the zone itself (2026-09-26: at 0.12 the obstructed cells were most of what
 *  made a zone read violet over the move band). */
const ZONE_TODO_FILL = "rgba(179, 140, 255, 0.07)";
const ZONE_TODO_STROKE = "rgba(224, 209, 255, 0.13)";
/** A `hidden` (GM-only) zone's dash — Recon's True CZ, or any concealed designation. This is a
 *  LABEL, not a filter: the server (`projectSitrepFor`) already strips hidden zones from every
 *  non-GM frame, so anything that reaches this renderer is a zone the viewer is entitled to see.
 *  Re-deriving that redaction here would be display-only masking over data already delivered. */
const ZONE_HIDDEN_DASH = 10;
const ZONE_HIDDEN_GAP = 6;

// The terrain layer's own colours — cell treatments, the hazard overlay, edge walls, area-effect
// clouds — are NOT inline here. They live in `terrainPalette.ts`, the single source of truth every
// surface that draws or explains terrain reads (`terrain-legibility` D7); the constants above are
// board chrome, which is this module's alone.

/** A slow, cheap "breathing" alpha drift (ticker-driven, transform/alpha only on already-built
 *  geometry — the same pattern the halo pulse/cursor lerp use) so a live cloud reads as something
 *  drifting in the air rather than a static painted tile, without any per-frame rebuild. */
const AREA_EFFECT_BREATH_PERIOD_S = 4.2;
const AREA_EFFECT_BREATH_MIN = 0.6;
const AREA_EFFECT_BREATH_RANGE = 0.4;

/** Remove and destroy every child of `c` — used before every rebuild so torn-down Graphics/Text free
 *  their GPU resources rather than leaking. */
function clearContainer(c: Container): void {
  for (let i = c.children.length - 1; i >= 0; i--) {
    const child = c.children[i];
    c.removeChildAt(i);
    child?.destroy({ children: true });
  }
}

/** Chop the segment `(x1,y1)-(x2,y2)` into alternating `dash`-length draw / `gap`-length skip pieces,
 *  adding each drawn piece as its own `moveTo`/`lineTo` sub-path on `g`. Caller strokes once after all
 *  segments are added — Pixi has no native dashed-stroke support (see module doc). Takes one options
 *  object (not 6 positional args) — `CODE_STYLE.md`'s `max-params` gate caps at 3. */
function dashedSegment(
  g: Graphics,
  seg: { x1: number; y1: number; x2: number; y2: number; dash: number; gap: number },
): void {
  const { x1, y1, x2, y2, dash, gap } = seg;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return;
  const ux = dx / len;
  const uy = dy / len;
  let pos = 0;
  let on = true;
  while (pos < len) {
    const step = Math.min(on ? dash : gap, len - pos);
    if (on) {
      g.moveTo(x1 + ux * pos, y1 + uy * pos).lineTo(x1 + ux * (pos + step), y1 + uy * (pos + step));
    }
    pos += step;
    on = !on;
  }
}

/** A dashed rectangle outline — the four edges of `(x,y,w,h)`, each chopped via {@link dashedSegment}. */
function dashedRect(g: Graphics, rect: { x: number; y: number; w: number; h: number; dash: number; gap: number }): void {
  const { x, y, w, h, dash, gap } = rect;
  dashedSegment(g, { x1: x, y1: y, x2: x + w, y2: y, dash, gap });
  dashedSegment(g, { x1: x + w, y1: y, x2: x + w, y2: y + h, dash, gap });
  dashedSegment(g, { x1: x + w, y1: y + h, x2: x, y2: y + h, dash, gap });
  dashedSegment(g, { x1: x, y1: y + h, x2: x, y2: y, dash, gap });
}

// ── Holo tokens (§3 rows 8-10) — faction silhouettes, the selection halo, name labels ─────────────
// The palette + the treatment itself (`tokenLook`) live in `./combatCells`, the pure module, so
// they are unit-testable without standing up Pixi — this file owns only the DRAWING. Colour is
// keyed on SIDE (`tokenLook`); the image and ring themselves are drawn by `pixiTokenSprite.ts`.
const OPEN_DOOR_OPACITY = 0.6;
const TOKEN_INK = "#e9e6df";
const TOKEN_LABEL_FONT = 10;
/** Pill padding around the single-line truncated label text — the
 *  `drawSizeLabel`/`buildCursorChip` pattern (rounded-rect low-alpha fill behind mono text), reused
 *  rather than copied verbatim so this module doesn't grow a third near-duplicate pill builder. */
const TOKEN_LABEL_PAD_X = 5;
const TOKEN_LABEL_PAD_Y = 3;
/** Square-cornered ink pills, the GMS//OS label voice. */
const TOKEN_LABEL_RADIUS = 0;
const TOKEN_LABEL_BG = 0x111111;
const TOKEN_LABEL_BG_ALPHA = 0.82;
/** Leader-line stroke alpha — drawn only when a label's solved `where` is `"displaced"` (pushed off
 *  the default under-token position). An `"attached"` or `"inside"` placement draws no line at all;
 *  see {@link LabelEntry}'s doc. */
const TOKEN_LABEL_LEADER_ALPHA = 0.55;
/**
 * Side of the little square drawn under a token whose label the solver HID (`where === "hidden"`) —
 * the "this unit has a name, the board just has no room for it right now" mark. Filled
 * {@link TOKEN_INK} at {@link TOKEN_LABEL_LEADER_ALPHA}, centred on the token's bottom edge (the
 * label anchor), no animation.
 *
 * Deliberately the simplest acceptable drawing rather than a designed affordance: a human eye is the
 * only thing that can judge whether it reads as a name-marker or as board noise, and that judgement
 * is Phase 3.3's eyeball round (`projects/crowded-label-placement.md`,
 * `docs/agents/memories/standing-directives.md` "Visual effects need a human in the loop"). 4px is
 * ~a tenth of a 40px cell — visible at default zoom, too small to be mistaken for a status pip. This
 * constant is the first dial to turn if it reads wrong.
 */
const HIDDEN_LABEL_TICK_PX = 4;
/** The generic ring, for marks that have no combatant (the pending-deploy preview). */
const GENERIC_IMAGE: TokenImage = { kind: "generic", tier: "generic", url: null, framed: false };
/** The masked ring — the safe default when no image ctx has arrived yet. */
const MASKED_IMAGE: TokenImage = { kind: "generic", tier: "masked", url: null, framed: false };
/** One full halo pulse, in seconds. Driven by the shared ticker, which only ever writes
 *  transform/alpha on already-built geometry (§3 row 8). */
const HALO_PERIOD_S = 1.6;
/** The selection halo's ring (ruling 6): cream on a dark under-ring, so it reads on any ground and
 *  never borrows a side's colour. It breathes less deeply than its glow, so it never fades out. */
const HALO_CREAM = "#fff6c8";
const HALO_WIDTH = 2.5;
const HALO_RING_MIN_ALPHA = 0.65;

/**
 * The active unit's beacon (`turn-presence` Decision 1 C): a second red hex ring outside the token's
 * still ACTIVE frame, pulsing on {@link BEACON_PERIOD_S}, so the unit whose turn it is reads from
 * across the board. ACTIVE red for every side (`decisions/0197` point 7), never the side colour. Still
 * at full strength under reduced motion.
 */
const BEACON_PERIOD_S = 1.2;
const BEACON_WIDTH = 3;
const BEACON_MIN_ALPHA = 0.3;

/**
 * The overlay bands (`terrain-tile-art` 5.1, ruling 6): every reach and path overlay is a zone over
 * the textured ground, as the Phase 1 prototype drew them — a light wash, a cell-exact outline with
 * softened corners ({@link bandGeometry}), and a dark under-stroke beneath a light edge so the edge
 * reads on any surface. Attack range is a dashed edge with no wash. These replaced per-cell dots,
 * which were tuned for the old dark deck and vanished on the new ground.
 */
type BandStyle = {
  /** The fill over the band's cells; none for attack range. */
  wash?: string;
  edge: string;
  width: number;
  /** `[on, off]` px: a dashed edge. */
  dash?: readonly [number, number];
  /** A small light pip on each cell centre, as the prototype's move range carried. */
  pips?: string;
  /** The under-stroke's alpha, when the band should be quieter than {@link BAND_UNDER}'s. */
  underAlpha?: number;
};
/** The dark stroke under every band edge, `grow` px wider than the edge. */
const BAND_UNDER = { color: "#06080c", alpha: 0.6, grow: 2.5 } as const;

/**
 * Stroke the path `draw` adds to `g` twice: `under`, then `top` over it. Pixi starts a fresh path
 * after every `stroke()`, so a chained second stroke would draw nothing; the geometry is added once
 * per pass instead.
 */
function strokeTwice(g: Graphics, draw: (g: Graphics) => void, passes: { under: StrokeInput; top: StrokeInput }): Graphics {
  draw(g);
  g.stroke(passes.under);
  draw(g);
  return g.stroke(passes.top);
}
const BAND_PIP_RADIUS = 2;
const MOVE_RANGE_BAND: BandStyle = { wash: "rgba(140, 210, 255, 0.14)", edge: "#a8e4ff", width: 2, pips: "rgba(225, 245, 255, 0.55)" };
/** The boost ring (`projects/token-command-menu.md`, v20): cells only a Boost reaches, beyond the
 *  remaining move. The move family, reading "move, further": a deeper blue with a dashed edge, clear
 *  of the zone violet it used to share (developer, 2026-09-26). Its edge runs round the outside of
 *  both, so the seam between them is the move band's edge alone. */
const BOOST_RANGE_BAND: BandStyle = { wash: "rgba(70, 130, 230, 0.13)", edge: "#6aa6ff", width: 2, dash: [8, 5] };
/** The Sensors (TECH-RANGE) reach — a hacker-green, and tech mode is a SINGLE green band, never two
 *  layered. A cooler teal than the annotation green (`ANNOTATION_STROKE.green`) so the two stay
 *  distinct if they ever co-display. */
const SENSOR_RANGE_BAND: BandStyle = { wash: "rgba(90, 220, 170, 0.12)", edge: "#8ff0c8", width: 2 };
/** The planned path: a brighter band inside the move range. */
const MOVE_PATH_BAND: BandStyle = { wash: "rgba(200, 236, 255, 0.30)", edge: "#eef9ff", width: 2 };
/** The part of the planned path only a folded Boost pays for: the path band in the boost ring's
 *  deeper blue and dash, so the trail reads "move, then Boost" the way the washes under it do. */
const MOVE_PATH_BOOST_BAND: BandStyle = { wash: "rgba(80, 140, 240, 0.30)", edge: "#a9c9ff", width: 2, dash: [8, 5] };
/** A Trampler's drag trail the drop would refuse (over the move left): the attack family's red. */
const MOVE_PATH_REFUSED_BAND: BandStyle = { wash: "rgba(255, 110, 90, 0.26)", edge: "#ffb09a", width: 2 };
/** Attack range: the boundary alone, dashed, so the ground and the targets inside stay clear. */
const ATTACK_RANGE_BAND: BandStyle = { edge: "#ff7a6a", width: 2.5, dash: [8, 5] };
/** A hovered AoE template's cells. */
/**
 * The terrain mechanics overlay's outline (`terrain-mechanics-overlay` ruling 4): a neutral light
 * dotted edge with no wash. Every hue is spent on the reach bands, so meaning rides on the labels,
 * and the short dots keep it from reading as attack range's red 8/5 dash.
 */
const MECHANICS_BAND: BandStyle = { edge: "rgba(236, 232, 222, 0.85)", width: 1.5, dash: [2, 4], underAlpha: 0.45 };
const MECHANICS_LABEL_INK = "rgba(240, 236, 226, 0.95)";
const ATTACK_AFFECTED_BAND: BandStyle = { wash: "rgba(255, 120, 90, 0.22)", edge: "#ffb09a", width: 2 };
/** A template cell line of sight doesn't reach: darkened, with a light rim. */
const LOS_VEIL = { fill: "rgba(6, 8, 12, 0.5)", border: "rgba(232, 238, 246, 0.5)", width: 1 } as const;
/** The PLAY-mode drag-ghost's flat translucency — a plain fade
 *  over anything fancier (`visual-effects-need-human-loop` precedent), applied on top of whatever
 *  {@link renderTokenLook} itself computed so a ghost always reads as "not the real token." */
const DRAG_GHOST_ALPHA = 0.45;
/**
 * The **pending directed-deploy section**'s translucency (`jericho-directed-placement` D1 follow-up) — deliberately
 * the SAME flat-fade convention as {@link DRAG_GHOST_ALPHA} rather than a new dashed/animated
 * treatment: this app already means "translucent copy of a real token silhouette" = *client-local,
 * not authoritative yet*, and a pending section is exactly that. Reusing the established signal
 * costs nothing and can't be misread; inventing a second "provisional" vocabulary could
 * (`visual-effects-need-human-loop` — ship simple over unvettable-fancy).
 *
 * A touch fainter than the drag ghost because a pending section persists on the board across
 * several seconds of aiming (a drag ghost is transient and tracks the cursor), so it has to sit
 * *under* the eye's attention rather than compete with the live legal-cell wash it's drawn over.
 */
const PENDING_DEPLOY_ALPHA = 0.38;
/** The three reach-band families a targeting affordance can belong to. */
export type TargetingKind = "attack" | "tech" | "move";
/** `"r, g, b"` of a `#rrggbb` colour. */
function rgbOf(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}
/**
 * Each targeting kind's colour, off the SAME edges the board's reach bands draw (attack red,
 * Sensors green, movement blue), so the command menu's targeting glow, banner stripe and the target
 * rings can never drift from the band they sit beside. `"r, g, b"` strings for `rgb()`/`rgba()`.
 */
export const TARGETING_RGB: Readonly<Record<TargetingKind, string>> = {
  attack: rgbOf(ATTACK_RANGE_BAND.edge),
  tech: rgbOf(SENSOR_RANGE_BAND.edge),
  move: rgbOf(MOVE_RANGE_BAND.edge),
};
/** A target ring's pulse period: slower while a row is only hovered, faster while armed. */
const TARGET_RING_PERIOD_MS = { hover: 1100, armed: 700 } as const;
/** A newly-ringed target's one-shot intro: this long, turning this far (a hex lands on itself), from this scale. */
const TARGET_RING_INTRO_MS = 350;
const TARGET_RING_INTRO_TURN = (2 * Math.PI) / 3;
const TARGET_RING_INTRO_SCALE = 0.7;
/** The READY peak mark's pick glow (`pixiTokenChrome.ts`'s `peak-glow`): its pulse period, its alpha
 *  range, and the steady alpha it holds under reduced motion. */
const PEAK_GLOW_PERIOD_MS = 1600;
const PEAK_GLOW_MIN = 0.25;
const PEAK_GLOW_STILL = 0.7;

/**
 * One legal target's mini forecast badge (`projects/target-mini-forecasts.md` 1.3): the armed
 * attack's net roll and hit chance, `@lancer-vtt/rules`' `miniForecast` strings. `kind` picks the
 * pill's border from {@link TARGETING_RGB}, the colour the target's ring is already drawn in.
 */
export type TargetBadgeSpec = { net: string; hit: string; kind: TargetingKind };
/** The text a badge draws. One place, so the board and its debug read agree. */
export function targetBadgeText(spec: TargetBadgeSpec): string {
  return `${spec.net} · ${spec.hit}`;
}
const TARGET_BADGE_FONT = 9;
const TARGET_BADGE_PAD_X = 4;
const TARGET_BADGE_PAD_Y = 2;

const ANNOTATION_STROKE: Record<AnnotationColor, string> = {
  cyan: "#5fe3ff",
  amber: "#f0c14b",
  pink: "#ff6cae",
  green: "#3ad29f",
};
const ANNOTATION_STROKE_WIDTH = 3;
const ANNOTATION_SELECTED_STROKE = "#ffffff";

const PING_BURST_MS = 1100;
const CURSOR_LERP_TAU_MS = 55;
const CURSOR_FADE_MS = 180;

// ── Float / projectile / impact VFX (§3's shared VFX pool) ───────────────────────────────────────
// Pooled into the same `vfxContainer` ping bursts use — one shared self-managing overlay tier,
// ticker-driven, no React state — mirroring `addPing`'s pattern exactly.
const FLOAT_RISE_MS = 900;
const FLOAT_RISE_DIST = CELL * 0.9;
const FLOAT_COLORS: Record<FloatTone, string> = {
  damage: "#ff6b6b",
  heal: "#69d98b",
  miss: "#9aa6b2",
  status: "#5cb8ff",
  verdict: "#ffd166",
  destroy: "#ff3b3b",
  caption: "#f4efe2",
};
/** A chain link's caption plate (`chained-resolution-sequence` 2.1): held still, then faded, rather than risen like a number. */
const CAPTION_HOLD_MS = 850;
const CAPTION_FADE_MS = 250;
const CAPTION_PLATE = "#14120e";
const CAPTION_RULE = "#f0c14b";
/** The chain link's focus ring (2.2): an amber circle just outside the token's footprint. */
const FOCUS_RING_COLOR = "#f0c14b";
const IMPACT_BURST_MS = 320;
/**
 * The quick-tech burst's palette — a hacker-green
 * recolor of the SAME `spawnImpact` fireball shape (outer ring → mid flare → hot core), never a new
 * particle system: tech doesn't detonate like a bullet impact, so the burst reads as an electric
 * pulse instead of a fireball purely through color, reusing every tween/lifecycle line the ballistic
 * burst already has (`impactEntries`, `IMPACT_BURST_MS`). Deliberately its own hue, not a literal
 * reuse of `SENSOR_RANGE_FILL`/`ANNOTATION_STROKE.green` — a brief, saturated flourish reads
 * differently than either persistent low-alpha wash; eyeball-tuned starting point (owed human pass).
 */
const TECH_BURST_RING = "#18e6a8";
const TECH_BURST_FLARE = "#3dffce";
const TECH_BURST_CORE = "#eafff5";

/** A stable, distinct colour per participant slot id — a hash → hue (ported verbatim from
 *  `CombatStage.tsx`'s pre-Phase-3 `slotHue`/`cursorColor`). */
function slotHue(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 360;
}

function cursorColor(id: string): string {
  return `hsl(${slotHue(id)}, 70%, 62%)`;
}

/**
 * Draw `points` into `g` as either straight segments (`smooth = false`, a polyline's deliberately-
 * clicked vertices) or a cheap quadratic-through-midpoints smoothing (`smooth = true`, a freehand
 * stroke) — Pixi's `Graphics` has no built-in curve-tension smoothing, so this approximates the
 * same "reads as a smoothed curve" effect via the standard midpoint-control-point technique. A
 * cosmetic approximation, not a pixel-identical port (same spirit as the board gradient's Gotcha).
 */
function drawSmoothLine(g: Graphics, points: { x: number; y: number }[], smooth: boolean): void {
  if (points.length < 2) return;
  const first = points[0]!;
  g.moveTo(first.x, first.y);
  if (!smooth || points.length < 3) {
    for (let i = 1; i < points.length; i++) {
      const p = points[i]!;
      g.lineTo(p.x, p.y);
    }
    return;
  }
  for (let i = 1; i < points.length - 1; i++) {
    const cur = points[i]!;
    const next = points[i + 1]!;
    g.quadraticCurveTo(cur.x, cur.y, (cur.x + next.x) / 2, (cur.y + next.y) / 2);
  }
  const last = points[points.length - 1]!;
  g.lineTo(last.x, last.y);
}

// ── Board background: placeholder fill + frame + ruler (§3 row 1) — built once per cols/rows ──────

function buildBoardContainer(cols: number, rows: number): Container {
  const boardW = Math.max(1, cols) * CELL;
  const boardH = Math.max(1, rows) * CELL;
  const container = new Container();

  // The hard brutalist drop-shadow (`--brutalist-shadow-lg`), then a flat fill under the terrain
  // layer, seen only until its first paint lands. The grid is baked into the terrain as corner
  // crosses (ruling 2), so there is no lattice here.
  const { offset } = FRAME_SHADOW;
  container.addChild(new Graphics().rect(offset, offset, boardW, boardH).fill({ color: FRAME_SHADOW.color, alpha: FRAME_SHADOW.alpha }));
  container.addChild(new Graphics().rect(0, 0, boardW, boardH).fill(BOARD_PLACEHOLDER));

  // The frame: one crisp rule just outside the board's edge.
  container.addChild(new Graphics().rect(-1, -1, boardW + 2, boardH + 2).stroke({ width: 2, color: FRAME_INK }));

  // Coordinate ruler: column letters above, row numbers to the left. `columnLabel` is SHARED with
  // the panels that name cells in prose (`lib/encounter/cellLabel.ts`) — a panel and this ruler
  // naming the same cell differently is the failure that module exists to prevent, so the origin
  // this loop establishes (column 0 → "A", row 0 → "1") is load-bearing beyond the board.
  const rulerStyle = new TextStyle({ fill: RULER_INK, fontSize: RULER_FONT, fontFamily: RULER_FONT_FAMILY });
  for (let c = 0; c < cols; c++) {
    const t = new Text({ text: columnLabel(c), style: rulerStyle });
    t.anchor.set(0.5, 1);
    t.x = c * CELL + CELL / 2;
    t.y = -5;
    container.addChild(t);
  }
  for (let r = 0; r < rows; r++) {
    const t = new Text({ text: String(r + 1), style: rulerStyle });
    t.anchor.set(1, 0.5);
    t.x = -4;
    t.y = r * CELL + CELL / 2;
    container.addChild(t);
  }

  return container;
}

/** The faint full grid PLAY offers as a view toggle (ruling 2): its own layer over the terrain, so
 *  toggling it never repaints a chunk. Drawn live, so it stays one device pixel thin at every zoom. */
function buildFullGrid(cols: number, rows: number): Graphics {
  const g = new Graphics();
  for (let c = 0; c <= cols; c++) g.moveTo(c * CELL, 0).lineTo(c * CELL, rows * CELL);
  for (let r = 0; r <= rows; r++) g.moveTo(0, r * CELL).lineTo(cols * CELL, r * CELL);
  return g.stroke({ ...FULL_GRID, pixelLine: true });
}

// ── Armed-attack cover hatch ─────────────────────────────────────────────────────────────────────

/** A 45° diagonal hatch over a faint wash, from a {@link HatchStyle} spec.
 *
 *  **Its callers are the armed-attack and hover-intel ring cover underlays** ({@link ARMED_COVER_HATCH}). Authored cover
 *  stopped being a hatch on 2026-09-01 and became an inset border, because pitch-and-alpha stops
 *  separating hard from soft at zoom-out; the overlay kept the hatch deliberately, because it answers
 *  a different question ("is this cell in cover relative to MY armed shot") and must not wear the same
 *  mark as the ground's own grade. */
function buildHatchCell(style: HatchStyle): Container {
  const { gap, alpha, wash } = style;
  const g = new Container();
  g.addChild(new Graphics().rect(0, 0, CELL, CELL).fill(wash));
  // Each diagonal is the segment of the slope-1 line `x = d + t, y = t` clipped analytically to the
  // cell's `[0,CELL]²` bounds — Pixi has no per-`Graphics` clip, so the trim is done in the math
  // instead of a mask.
  const hatch = new Graphics();
  const lines = (h: Graphics) => {
    for (let d = -CELL; d <= CELL; d += gap) {
      const tStart = Math.max(0, -d);
      const tEnd = Math.min(CELL, CELL - d);
      if (tEnd > tStart) h.moveTo(d + tStart, tStart).lineTo(d + tEnd, tEnd);
    }
  };
  strokeTwice(hatch, lines, {
    under: { width: style.hatchWidth + BAND_UNDER.grow / 2, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha * alpha },
    top: { width: style.hatchWidth, color: style.hatch, alpha },
  });
  g.addChild(hatch);
  return g;
}

// ── Rooms (§3 row 2) — render-only; the dblclick-to-inspect hit-test lives in CombatStage.tsx ────

function buildRoomContainer(room: Room): Container | null {
  const first = room.rects[0];
  if (!first) return null;
  const anchor = room.rects.reduce((best, r) => (r.y < best.y || (r.y === best.y && r.x < best.x) ? r : best), first);
  const cells = roomFloorCells(room);
  const volume = roomVolume(room);
  const boundary = roomBoundaryEdges(room);

  const c = new Container();

  const floorGlow = new Graphics();
  const floor = new Graphics();
  for (const r of room.rects) {
    floorGlow.rect(r.x * CELL, r.y * CELL, r.w * CELL, r.h * CELL).fill({ color: ROOM_FLOOR_GLOW, alpha: 0.1 });
    floor.rect(r.x * CELL, r.y * CELL, r.w * CELL, r.h * CELL).fill(ROOM_FLOOR_FILL);
  }
  floorGlow.filters = [new BlurFilter({ strength: 8 })];
  c.addChild(floorGlow, floor);

  const border = strokeTwice(
    new Graphics(),
    (g) => {
      for (const e of boundary) g.moveTo(e.x1 * CELL, e.y1 * CELL).lineTo(e.x2 * CELL, e.y2 * CELL);
    },
    { under: { width: 1.5 + BAND_UNDER.grow, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha }, top: { width: 1.5, color: ROOM_FLOOR_BORDER } },
  );
  c.addChild(border);

  const nameText = new Text({
    text: room.name,
    style: new TextStyle({ fill: ROOM_LABEL_INK, fontFamily: RULER_FONT_FAMILY, fontWeight: "bold", fontSize: ROOM_LABEL_NAME_FONT, stroke: LABEL_UNDER }),
  });
  nameText.x = anchor.x * CELL + ROOM_LABEL_PAD;
  nameText.y = anchor.y * CELL + ROOM_LABEL_PAD;
  c.addChild(nameText);

  const subText = new Text({
    text: `${cells}□ · ${volume} m³`,
    style: new TextStyle({ fill: ROOM_LABEL_SUB, fontFamily: RULER_FONT_FAMILY, fontSize: ROOM_LABEL_SUB_FONT, stroke: LABEL_UNDER }),
  });
  subText.x = anchor.x * CELL + ROOM_LABEL_PAD;
  subText.y = anchor.y * CELL + ROOM_LABEL_PAD + ROOM_LABEL_NAME_FONT + 3;
  c.addChild(subText);

  return c;
}

// ── Sitrep zones (§3 row 3.6) — the first browser surface for zones (`terraformer-encounter` 3.4) ─
//
// `sitrep-engine` shipped the whole mechanical layer (`SitrepZone`, six predicates, `seedSitrep`, CLI
// authoring, and the fog-of-war projection that strips `hidden` zones from player frames) with
// `CAPABILITIES.md` recording the gap plainly: "no browser authoring/HUD surface." This is the render
// half of closing it. Render-only — there is no zone authoring gesture here; `lvtt encounter sitrep`
// stays the authoring surface.
//
// **Read what the server's redaction actually does before touching the `hidden` branch below.**
// `projectSitrepFor` (`packages/domain/src/encounter/projection.ts`) strips the `hidden` FLAG off
// every zone for a non-GM viewer — it does NOT remove the zone. `id`/`kind`/`rects` all survive into
// a player's frame (pinned by `projection.test.ts`: *"player projection omits `hidden` from every
// zone, while ids/kinds/rects survive"*). That is the mechanic it was built for: Recon's players see
// all four Control Zones and just don't know which is the secretly designated True CZ.
//
// So this function draws the zone for EVERYONE, and the `hidden` branch only *decorates* — a dashed
// border + a "GM ONLY" tag, which reaches the GM alone because the flag is the one thing redacted.
// Do NOT "fix" this into skipping hidden zones for players: that would hide geometry the server
// deliberately delivers, break Recon's whole read, and be display-only masking besides.

/** What `setSitrepZones` needs per zone — the authored geometry plus, for a `clear` zone, its live
 *  clearance. `clearance` is `null` for every other kind (nothing to tally). Resolved by the caller
 *  (`CombatStage.tsx` off `@lancer-vtt/rules`' `zoneClearance`) so this module only draws. */
export type SitrepZoneRender = {
  zone: SitrepZone;
  clearance: ZoneClearance | null;
};


/** The cell keys (`"x,y"`) a rect covers. */
function rectCellKeys(r: { x: number; y: number; w: number; h: number }): string[] {
  const out: string[] = [];
  for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) out.push(`${x},${y}`);
  return out;
}

function buildSitrepZoneContainer(spec: SitrepZoneRender): Container | null {
  const { zone, clearance } = spec;
  const first = zone.rects[0];
  if (!first) return null;
  const anchor = zone.rects.reduce((best, r) => (r.y < best.y || (r.y === best.y && r.x < best.x) ? r : best), first);

  const c = new Container();

  // The band needs only a board that holds the zone's cells; the zone's own extent is one.
  const keys = zone.rects.flatMap((r) => rectCellKeys(r));
  const board = { cols: Math.max(...zone.rects.map((r) => r.x + r.w)), rows: Math.max(...zone.rects.map((r) => r.y + r.h)) };
  const dash = zone.hidden === true ? ([ZONE_HIDDEN_DASH, ZONE_HIDDEN_GAP] as const) : undefined;
  drawBand(c, keys, { ...board, style: { ...ZONE_BAND, dash } });
  // Still-obstructed cells of a `clear` zone — drawn ABOVE the wash so "what's left to bulldoze"
  // is the thing that reads, not the zone outline. Nothing is drawn once the zone is flat, so the
  // board going quiet IS the win signal.
  if (clearance && clearance.obstructedCells.length > 0) {
    const todo = new Graphics();
    for (const cell of clearance.obstructedCells) todo.rect(cell.x * CELL, cell.y * CELL, CELL, CELL);
    todo.fill(ZONE_TODO_FILL).stroke({ width: 1, color: ZONE_TODO_STROKE });
    c.addChild(todo);
  }

  const nameText = new Text({
    text: `${ZONE_KIND_LABEL[zone.kind]}${zone.hidden === true ? " · GM ONLY" : ""}`,
    style: new TextStyle({ fill: ZONE_LABEL_INK, fontFamily: RULER_FONT_FAMILY, fontWeight: "bold", fontSize: ROOM_LABEL_NAME_FONT, stroke: LABEL_UNDER }),
  });
  nameText.x = anchor.x * CELL + ROOM_LABEL_PAD;
  nameText.y = anchor.y * CELL + ROOM_LABEL_PAD;
  c.addChild(nameText);

  // The `clear` zone's live progress, right on the board. Every other kind shows its id instead —
  // there is nothing to tally, and the id is what `lvtt encounter sitrep show` prints.
  const sub =
    clearance && clearance.total > 0
      ? `${clearance.clear}/${clearance.total} CLEAR · ${clearancePercent(clearance)}%`
      : zone.id;
  const subText = new Text({
    text: sub,
    style: new TextStyle({ fill: ZONE_LABEL_SUB, fontFamily: RULER_FONT_FAMILY, fontSize: ROOM_LABEL_SUB_FONT, stroke: LABEL_UNDER }),
  });
  subText.x = anchor.x * CELL + ROOM_LABEL_PAD;
  subText.y = anchor.y * CELL + ROOM_LABEL_PAD + ROOM_LABEL_NAME_FONT + 3;
  c.addChild(subText);

  return c;
}

// ── Area effects (§3 row 2.5) — live `AreaEffect` clouds ────────
//
// One record per cloud (D2) storing a template (`origin` +
// `shape` + `size`), materialized to cells here via `squareSpace.disk` — the SAME grid-agnostic
// geometry `areaContributions.ts` (`@lancer-vtt/domain`) folds into the cover query, so what's drawn
// here is exactly the set of cells that grant cover, never an approximation of it. `setAreaEffects`
// rebuilds this container's children from scratch on every state change (this file's default
// convention — tokens are the one deliberate exception), so a dispersed cloud's Graphics
// are destroyed via `clearContainer`, never left orphaned on screen. Deliberately NOT tile art
// (smoke must not read as painted terrain) and NOT an object token (it isn't a destructible thing) — see `terrainPalette.ts`'s
// `AREA_EFFECT_STYLE` doc; a shield dome draws with `SHIELD_DOME_STYLE` (`areaEffectStyle`).

/** One live cloud's fill — a soft blurred wash over its disk's cells (v1: `shape: "disk"` only, D1). */
function buildAreaEffectFill(cells: readonly { x: number; y: number }[], style: AreaEffectStyle): Graphics {
  const fill = new Graphics();
  for (const cell of cells) fill.rect(cell.x * CELL, cell.y * CELL, CELL, CELL);
  fill.fill(style.fill);
  // The blur softens the disk's grid-square silhouette into something that reads as gaseous rather
  // than a crisp painted tile — the SAME `BlurFilter` bloom trick used elsewhere in this file (the
  // bezel/wall glow/room floor glow), applied here to the fill itself rather than a glow duplicate.
  fill.filters = [new BlurFilter({ strength: style.blur })];
  return fill;
}

/** The cells an area effect covers, as `squareSpace.disk` materializes them. */
function areaEffectCells(area: AreaEffect): { x: number; y: number }[] {
  return [...squareSpace.disk(asSquareCell(area.origin), area.size).cells()];
}

/** The cloud's exact extent over the blur (`terrain-tile-art` 5.1): the cells that grant cover, as a
 *  fine dashed band edge with no wash of its own. */
const AREA_EFFECT_EDGE: BandStyle = { edge: "rgba(236, 240, 244, 0.8)", width: 1.5, dash: [4, 4] };
/** A shield dome's edge: a solid cyan shell, not a cloud's dashed band. */
const SHIELD_DOME_EDGE: BandStyle = { edge: "rgba(120, 220, 255, 0.9)", width: 2 };
/** A drone's aura (`ll1-drones` 9.1): an amber dashed edge around its Burst, no wash, so it reads as a
 *  field that moves with the drone rather than a cloud or painted terrain. */
const DRONE_AURA_EDGE: BandStyle = { edge: "rgba(255, 196, 92, 0.85)", width: 1.5, dash: [6, 4] };

// ── Authoring-preview ghosts (§3 row 14) — tile box/hover/stroke, wall run, room run ──────────────
//
// `CombatStage.tsx` resolves all the direction/color decisions (via the unchanged pure helpers in
// `combatAuthoring.ts`/`combatCells.ts`) and hands this module a fully-resolved, renderer-agnostic
// spec — this module only draws. Coordinates on `"box"`/`"line"` are already world px (× CELL); `"cells"`
// carries raw cell indices, each bounds-checked per cell before drawing.

/** `swatch` is the cell a tile paint would write: the ghost draws its swatch per cell (`terrain-tile-art`
 *  5.5). `null` draws the flat `fill` (a room, or an erase). */
export type AuthoringPreviewSpec =
  | { kind: "cells"; cells: { x: number; y: number }[]; add: boolean; swatch: TerrainCell | null; cols: number; rows: number }
  | { kind: "box"; x: number; y: number; w: number; h: number; fill: string; opacity: number; stroke: string; label: string; swatch: TerrainCell | null }
  | { kind: "line"; x1: number; y1: number; x2: number; y2: number; stroke: string };

const PREVIEW_ADD_FILL_FALLBACK = "rgba(95, 227, 255, 0.34)";
const PREVIEW_ADD_STROKE_FALLBACK = "#c9a23e";
const REMOVE_FILL_FALLBACK = "rgba(192, 106, 106, 0.20)";
const REMOVE_STROKE_FALLBACK = "#c06a6a";

/** How opaque a tile ghost's swatch is: the ground under it still reads, so it reads as a preview. */
const GHOST_SWATCH_ALPHA = 0.6;

/** One GPU texture per painted `fill` swatch, for the session — the swatches themselves are cached
 *  the same way (`terrainArt/swatches.ts`). */
const ghostTextures = new Map<string, Texture>();

/** The ghost texture for painting `cell`, or `null` until its swatch has painted. */
function ghostTexture(cell: TerrainCell): Texture | null {
  const spec: SwatchSpec = { kind: "tile", cell, shape: "fill" };
  const key = swatchKey(spec);
  const hit = ghostTextures.get(key);
  if (hit) return hit;
  const swatch = tileSwatches.peek(spec);
  if (!swatch) return null;
  const texture = new Texture({ source: new ImageSource({ resource: swatch.bitmap }) });
  ghostTextures.set(key, texture);
  return texture;
}

/** A tile ghost cell: the swatch at {@link GHOST_SWATCH_ALPHA}, or the flat add tint until it paints. */
function ghostCell(container: Container, at: { x: number; y: number; texture: Texture | null }): void {
  if (at.texture) {
    const sprite = new Sprite(at.texture);
    sprite.x = at.x;
    sprite.y = at.y;
    sprite.width = CELL;
    sprite.height = CELL;
    sprite.alpha = GHOST_SWATCH_ALPHA;
    container.addChild(sprite);
  } else {
    container.addChild(new Graphics().rect(at.x, at.y, CELL, CELL).fill({ color: PREVIEW_ADD_FILL_FALLBACK, alpha: 0.5 }));
  }
}

function drawCellsPreview(container: Container, spec: Extract<AuthoringPreviewSpec, { kind: "cells" }>): void {
  const { cells, add, cols, rows } = spec;
  const texture = add && spec.swatch ? ghostTexture(spec.swatch) : null;
  const eraseG = new Graphics();
  const strokeG = new Graphics();
  for (const cell of cells) {
    if (cell.x < 0 || cell.x >= cols || cell.y < 0 || cell.y >= rows) continue;
    const x = cell.x * CELL;
    const y = cell.y * CELL;
    if (add) ghostCell(container, { x, y, texture });
    else eraseG.rect(x, y, CELL, CELL).fill({ color: REMOVE_FILL_FALLBACK, alpha: 1 });
    dashedRect(strokeG, { x, y, w: CELL, h: CELL, dash: 6, gap: 4 });
  }
  strokeG.stroke({ width: 1.5, color: add ? PREVIEW_ADD_STROKE_FALLBACK : REMOVE_STROKE_FALLBACK });
  container.addChild(eraseG, strokeG);
}

function drawSizeLabel(container: Container, spec: { x: number; y: number; w: number; label: string; color: string }): void {
  const { x, y, w, label, color } = spec;
  const labelG = new Container();
  labelG.x = x + w + 6;
  labelG.y = y;
  labelG.addChild(new Graphics().rect(0, 0, label.length * 8 + 12, 18).fill({ color: TOKEN_LABEL_BG, alpha: 0.85 }));
  const text = new Text({ text: label, style: new TextStyle({ fill: color, fontSize: 12, fontWeight: "bold", fontFamily: RULER_FONT_FAMILY }) });
  text.x = 6;
  text.y = 3;
  labelG.addChild(text);
  container.addChild(labelG);
}

function drawBoxPreview(container: Container, spec: Extract<AuthoringPreviewSpec, { kind: "box" }>): void {
  const { x, y, w, h, fill, opacity, stroke, label } = spec;
  const texture = spec.swatch ? ghostTexture(spec.swatch) : null;
  const g = new Graphics();
  if (texture) {
    for (let cy = y; cy < y + h; cy += CELL) for (let cx = x; cx < x + w; cx += CELL) ghostCell(container, { x: cx, y: cy, texture });
  } else {
    g.rect(x, y, w, h).fill({ color: fill, alpha: opacity });
  }
  dashedRect(g, { x, y, w, h, dash: 10, gap: 6 });
  g.stroke({ width: 2, color: stroke });
  container.addChild(g);
  drawSizeLabel(container, { x, y, w, label, color: stroke });
}

function drawLinePreview(container: Container, spec: Extract<AuthoringPreviewSpec, { kind: "line" }>): void {
  const { x1, y1, x2, y2, stroke } = spec;
  const g = new Graphics();
  dashedSegment(g, { x1, y1, x2, y2, dash: 9, gap: 6 });
  g.stroke({ width: 5, color: stroke, alpha: 0.85, cap: "round" });
  container.addChild(g);
}

// ── Tokens (§3 row 9) — one Container per placed combatant, built fresh on every `setTokens` ──────
//
// No Pixi `hitArea`/`eventMode` here — deliberately (the ratified native-DOM + pure-math interaction
// model). Click-select / drag-start hit-testing lives in
// `CombatStage.tsx` via `combatCells.ts`'s `hitTestToken`, pure footprint-AABB math against the
// native pointer event; this module only draws + exposes `moveToken` for the live drag to mutate.

/**
 * One built token's geometry, kept so `moveToken`/the per-frame follow (labels, the halo) can read
 * its LIVE position (which may differ from the resting `restMinX,restMinY` mid-drag/mid-tween)
 * without a rebuild.
 *
 * `restMinX`/`restMinY` is `setTokens`' OWN record of the last authoritative footprint anchor it
 * positioned this container at — deliberately distinct from the container's LIVE `.x`/`.y`, which an
 * in-flight `pixiAnimator.ts` tween or a local `moveToken` drag may be actively writing every frame.
 * The incremental reconcile (below) compares a persisting token's freshly-computed anchor against
 * `restMinX/Y` — never against the live position — so it only repositions the container when
 * presentation's `pos` has genuinely changed (a real commit or a hard snap), and never mid-flight.
 * **A destroy-and-rebuild-everything `setTokens` is the trap this avoids:** it would fire on any
 * `combatants`-array identity change (`CombatApp.tsx`'s `Object.values(mapState.combatants)` mints a
 * fresh array on every render, far more often than "a token actually moved"), racing whichever
 * tween/drag was live and snapping a forced/remote move's slide back to the start.
 *
 * `styleKey` is a cheap fingerprint of every OTHER visual input (kind/destroyed/openDoor/concealed/
 * untargetable/selected/name/footprint size/resolved image/turn-state + vitals chrome) so a `setTokens` call triggered by unrelated state (the
 * same non-memoized `combatants` re-render above) skips rebuilding a token's child graphics when
 * nothing about its look actually changed.
 */
type TokenEntry = { container: Container; side: number; accent: string; restMinX: number; restMinY: number; styleKey: string; mine: boolean; art: TokenArtState; chrome: TokenChrome | undefined };

/**
 * One built token label — a per-token `Container` holding a
 * rounded-rect pill background, the single-line truncated `Text`, a leader-line `Graphics` and a
 * hidden-marker tick `Graphics`.
 * `container.x`/`.y` is the pill's world-space TOP-LEFT corner (the bg is always drawn from local
 * `(0,0)` to `(w,h)`) — so a reader (the ticker follow, {@link CombatBoardHandle.debugLabelBounds})
 * never needs a second offset/anchor calculation.
 *
 * `dx`/`dy`/`where`/`slot` are `labelLayout.ts`'s `solveLabelLayout` output, re-solved once per
 * `setTokens` call (never per frame): `dx`/`dy` is the offset from the token's RESTING top-left
 * corner to this label's top-left corner, and the per-frame ticker-follow re-applies that fixed
 * offset against the token's LIVE position every frame, so a local drag/animator tween carries the
 * whole callout rigidly.
 *
 * `where` is the placement KIND (`labelLayout.ts`'s `PlacedWhere`), and it is the renderer's whole
 * drawing rule:
 *
 *   - **leader line iff `where === "displaced"`**. `"inside"` (a size-2+ unit's label within its own
 *     footprint) is already sitting on the thing it names, and `"attached"` (candidate 0, the plain
 *     under-token position, the only candidate on the small `GAP`) has nothing for a line to span —
 *     so neither draws one. A sparse board of size-1 units is all `"attached"`: no leader line and
 *     no pill offset at all.
 *   - **pill + text hidden, {@link HIDDEN_LABEL_TICK_PX} tick drawn, iff `where === "hidden"`** — the
 *     solver found no collision-free candidate and declined to draw the name on top of something.
 *     `dx`/`dy` is then the label ANCHOR (candidate 0), so the tick lands just under the token's
 *     bottom centre and the hover/selection reveal has somewhere to put the pill.
 *     {@link CombatBoardHandle.debugLabelBounds} reports NO rect for such an entry, which is what
 *     keeps `frontend/e2e/label-layout.e2e.ts`'s "no label occludes a foreign token" honest.
 *
 * `slot` is the truthful candidate INDEX behind that kind — `0..7` the eight compass slots, `8` and
 * up the positions slid along a side, `undefined` for an inside or hidden one (never a sentinel; see
 * `labelLayout.ts`'s `PlacedLabel` doc). The renderer never draws off it; it exists to be fed
 * straight back in as the next solve's `prevSlot`, which is how a settled label stays put when an
 * unrelated token moves, and `undefined` correctly reads there as "no remembered placement".
 *
 * `w`/`h` is the pill's own measured size (`text.width/.height` + padding, cached here rather than
 * re-read off `bg.width/.height` every solve) — the label size the solver consumes AND the rect
 * {@link CombatBoardHandle.debugLabelBounds} reports, since it's the literal size the pill was drawn
 * at.
 */
type LabelEntry = { container: Container; bg: Graphics; text: Text; leader: Graphics; tick: Graphics; w: number; h: number; dx: number; dy: number; where: PlacedWhere; slot: number | undefined; onReveal: boolean };

/** One drawn mini forecast badge: a pill like {@link LabelEntry}'s, with `dx`/`dy`/`where`/`slot`
 *  from `targetBadgeLayout.ts`'s `placeTargetBadges`, relative to the target's resting top-left and
 *  followed per frame the same way. */
type BadgeEntry = { container: Container; bg: Graphics; text: Text; spec: TargetBadgeSpec; w: number; h: number; dx: number; dy: number; where: PlacedWhere; slot: number | undefined };

/** Refresh a label's pill background + text in place — the single seam both the CREATE and RESTYLE
 *  branches of `setTokens` call, so the two can never draw the pill differently. `text` comes from
 *  `assetLook.ts`'s `tokenLabelText`, which truncates a unit's name (render-side only — `c.name`
 *  itself is never mutated, so `deployables.e2e.ts`'s panel locators, which key off the FULL name,
 *  are untouched). Mutates `entry.w`/`.h` to the freshly-measured pill size; the caller re-solves
 *  layout afterward. */
function layoutLabelPill(entry: LabelEntry, text: string): void {
  if (entry.text.text !== text) entry.text.text = text;
  entry.w = entry.text.width + TOKEN_LABEL_PAD_X * 2;
  entry.h = entry.text.height + TOKEN_LABEL_PAD_Y * 2;
  entry.bg.clear().roundRect(0, 0, entry.w, entry.h, TOKEN_LABEL_RADIUS).fill({ color: TOKEN_LABEL_BG, alpha: TOKEN_LABEL_BG_ALPHA });
  entry.text.x = TOKEN_LABEL_PAD_X;
  entry.text.y = TOKEN_LABEL_PAD_Y;
}

/**
 * A map asset's label (`projects/map-assets.md` 3.2): shown only on hover or selection, so a wooded
 * board isn't a wall of names, and never fed to the solver, so trees don't push units' names around.
 * It sits inside the bottom of a size-2+ footprint and just under a size-1 one, over the art.
 * `where: "hidden"` keeps it out of the badge solve; {@link syncLabelReveal} draws no tick for it.
 */
function placeRevealOnlyLabel(entry: LabelEntry, side: number): void {
  entry.dx = (side - entry.w) / 2;
  entry.dy = side > CELL ? side - entry.h - 4 : side + 2;
  entry.where = "hidden";
  entry.slot = undefined;
  entry.leader.clear();
  entry.tick.clear();
}

/** The point on `r`'s boundary nearest `p` — `p` is always outside `r` for every caller here (a
 *  label pill and its token are never allowed to overlap by the solver's own `GAP`), so a plain
 *  clamp-to-bounds lands exactly on the edge, never the interior. Used to build the leader line's
 *  two endpoints (pill edge nearest the token, token edge nearest the pill) below. */
function nearestBoundaryPoint(r: { x: number; y: number; w: number; h: number }, p: { x: number; y: number }): { x: number; y: number } {
  return { x: Math.max(r.x, Math.min(p.x, r.x + r.w)), y: Math.max(r.y, Math.min(p.y, r.y + r.h)) };
}

/** `destroyed`/`openDoor`/`hidden` derive from `c.kind`/`c.statuses` alone — shared by
 *  {@link tokenStyleKey} (to fingerprint them) and {@link renderTokenLook} (to draw them), so the two
 *  never disagree. `hidden` is RAW `Hidden` (`HIDDEN_STATUS`), NOT `c.concealed` — the GM's fog
 *  concealment is a separate, independently-drawn axis (`projection.ts`'s module doc). */
function combatantLookFlags(c: Combatant): { destroyed: boolean; openDoor: boolean; hidden: boolean } {
  const destroyed = (c.kind === "object" || c.kind === "deployable") && hasStatus(c.statuses, "Destroyed");
  const openDoor = !destroyed && isOpenDoor(c);
  const hidden = hasStatus(c.statuses, HIDDEN_STATUS);
  return { destroyed, openDoor, hidden };
}

/** Per-token render inputs that come from the caller rather than the combatant. `mine` = the viewer
 *  controls this unit (owner or control grant); it is in the style key so a live grant repaints.
 *  `image` is the resolved token art (`tokenImageFor`), keyed in so an upload or unmask repaints. */
type TokenLookOpts = { untargetable: boolean; selected: boolean; mine: boolean; image: TokenImage; chrome: TokenChrome | undefined; ghost?: boolean };

/** Fingerprint of every visual input {@link renderTokenLook} depends on OTHER than position — see
 *  {@link TokenEntry}'s doc for why `setTokens` compares this instead of always rebuilding. */
function tokenStyleKey(c: Combatant, opts: TokenLookOpts): string {
  const { destroyed, openDoor, hidden } = combatantLookFlags(c);
  return [c.kind, c.base.allegiance, destroyed, openDoor, hidden, c.concealed, opts.untargetable, opts.selected, opts.mine, tokenLabelText(c), c.base.size, opts.image.tier, opts.image.url, opts.image.glyph, tokenChromeKey(opts.chrome)].join("|");
}

/**
 * Clear and rebuild `container`'s children (`pixiTokenSprite.ts`'s image-in-a-ring) + alpha from
 * `c`'s current look. Deliberately NEVER touches `container.x`/`.y` — position is the reconcile's job
 * in `setTokens`, kept strictly separate so a live drag's or animator tween's writes to the container
 * are never fought (see {@link TokenEntry}'s doc). Returns the geometry a persisting entry needs to
 * remember (`side`/`accent`, for the halo + label sizing) and the art state for the debug hook.
 */
function renderTokenLook(container: Container, c: Combatant, opts: TokenLookOpts): { side: number; accent: string; art: TokenArtState } {
  const sq = asSquareCell(c.pos!);
  const { n } = footprintBox(sq, c.base.size);
  const side = n * CELL;
  const { destroyed, openDoor, hidden } = combatantLookFlags(c);
  const look = tokenLook(c, destroyed);

  clearContainer(container);
  container.alpha =
    (opts.untargetable ? 0.3 : 1) *
    (c.concealed ? 0.5 : 1) *
    (destroyed ? 0.55 : 1) *
    (openDoor ? OPEN_DOOR_OPACITY : 1);

  // A map asset is drawn by the asset layer under everything (`terrainArt/assetLayer.ts`); its token
  // stays empty, so no disc, ring or chrome sits on the tree. A drag ghost outlines the footprint.
  if (isDrawnAsAsset(c)) {
    if (opts.ghost) {
      const outline = new Graphics();
      dashedRect(outline, { x: 1, y: 1, w: side - 2, h: side - 2, dash: 6, gap: 4 });
      container.addChild(outline.stroke({ width: 1.5, color: HALO_CREAM }));
    }
    return { side, accent: look.accent, art: { tier: opts.image.tier, url: null, loaded: false } };
  }

  if (opts.chrome) drawTokenChromeUnder(container, opts.chrome, { side, cell: CELL });
  const art = drawTokenSprite(container, {
    side,
    image: opts.image,
    accent: look.accent,
    fill: look.fill,
    selected: opts.selected,
    mine: opts.mine,
    destroyed,
    hidden,
    concealed: c.concealed === true,
    openDoor,
    spent: opts.chrome?.state === "activated",
  });
  if (opts.chrome) drawTokenChrome(container, opts.chrome, { side, cell: CELL, accent: look.accent });
  return { side, accent: look.accent, art };
}

/** Flatten `[x, y]` points for Pixi's `poly`. */
function flat(points: readonly (readonly [number, number])[]): number[] {
  return points.flatMap((p) => [p[0], p[1]]);
}

/**
 * Draw the cells of `keys` into `container` as one overlay band in `style` ({@link BandStyle}): a
 * wash per filled piece, the dark under-stroke, the light edge (dashed if the style says so), and
 * the pips. `edgeKeys`, when given, is the set whose outline carries the edge instead of `keys`'
 * own: the boost band washes its own cells but edges the whole reach. Off-board keys are skipped;
 * nothing is added when no cell is on the board.
 */
function drawBand(
  container: Container,
  keys: Iterable<string>,
  opts: { cols: number; rows: number; style: BandStyle; edgeKeys?: Iterable<string> },
): void {
  const { cols, rows, style } = opts;
  const board = { cols, rows };
  const own = [...keys];
  const shape = bandGeometry(own, board);
  if (shape.loops.length === 0) return;
  if (style.wash) {
    // One Graphics per piece: Pixi's `cut` also punches the previous fill of the same Graphics.
    for (const piece of shape.pieces) {
      const g = new Graphics().poly(flat(piece.outer)).fill(style.wash);
      for (const hole of piece.holes) g.poly(flat(hole)).cut();
      container.addChild(g);
    }
  }
  const loops = opts.edgeKeys ? bandGeometry(opts.edgeKeys, board).loops : shape.loops;
  const runs = style.dash ? loops.flatMap((L) => dashLoop(L, style.dash![0], style.dash![1]).map((run) => ({ pts: flat(run), closed: false }))) : loops.map((L) => ({ pts: flat(L), closed: true }));
  const edge = strokeTwice(
    new Graphics(),
    (g) => {
      for (const r of runs) g.poly(r.pts, r.closed);
    },
    {
      under: { width: style.width + BAND_UNDER.grow, color: BAND_UNDER.color, alpha: style.underAlpha ?? BAND_UNDER.alpha, join: "round" },
      top: { width: style.width, color: style.edge, join: "round" },
    },
  );
  container.addChild(edge);
  if (style.pips) {
    const pips = new Graphics();
    for (const [x, y] of cellCentres(own, board)) pips.circle(x, y, BAND_PIP_RADIUS);
    container.addChild(pips.fill(style.pips));
  }
}

/**
 * The persisted draw-annotations render (§3 row 11) — every stored {@link Annotation}, a glow
 * duplicate underneath a crisp stroke (freehand smoothed via {@link drawSmoothLine}, polyline
 * straight-segmented). `selectedId`'s stroke renders brighter/thicker + a white outline. Selection
 * hit-testing is native-DOM + pure math (`combatDrawing.ts`'s `hitTestAnnotation`), not drawn here.
 */
function buildAnnotationsContainer(annotations: Record<AnnotationId, Annotation>, selectedId: AnnotationId | null): Container {
  const container = new Container();
  for (const a of Object.values(annotations)) {
    const selected = a.id === selectedId;
    const stroke = selected ? ANNOTATION_SELECTED_STROKE : ANNOTATION_STROKE[a.color];
    const width = selected ? ANNOTATION_STROKE_WIDTH + 2 : ANNOTATION_STROKE_WIDTH;
    const smooth = a.kind === "freehand";
    const glow = new Graphics();
    drawSmoothLine(glow, a.points, smooth);
    glow.stroke({ width: width + (selected ? 8 : 5), color: stroke, alpha: selected ? 0.55 : 0.4, cap: "round", join: "round" });
    glow.filters = [new BlurFilter({ strength: selected ? 5 : 3 })];
    const line = strokeTwice(new Graphics(), (g) => drawSmoothLine(g, a.points, smooth), {
      under: { width: width + BAND_UNDER.grow, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha, cap: "round", join: "round" },
      top: { width, color: stroke, cap: "round", join: "round" },
    });
    container.addChild(glow, line);
  }
  return container;
}

/** The in-progress **draw** preview (§3 row 12) — an open freehand drag (smoothed, matching the
 *  committed render) and/or an accumulating polyline (committed vertices as dots + a dashed rubber-
 *  band segment from the last vertex to the live cursor). */
export type DrawPreviewSpec = {
  color: AnnotationColor;
  freehandPreview: { x: number; y: number }[] | null;
  polylinePreview: { x: number; y: number }[] | null;
  polylineCursor: { x: number; y: number } | null;
};

function buildDrawPreviewContainer(spec: DrawPreviewSpec): Container {
  const container = new Container();
  const stroke = ANNOTATION_STROKE[spec.color];
  if (spec.freehandPreview && spec.freehandPreview.length >= 2) {
    const g = new Graphics();
    drawSmoothLine(g, spec.freehandPreview, true);
    g.stroke({ width: ANNOTATION_STROKE_WIDTH, color: stroke, alpha: 0.7, cap: "round", join: "round" });
    container.addChild(g);
  }
  const rubberBand = spec.polylinePreview && spec.polylineCursor ? [...spec.polylinePreview, spec.polylineCursor] : spec.polylinePreview;
  if (rubberBand && rubberBand.length >= 2) {
    const g = new Graphics();
    for (let i = 0; i < rubberBand.length - 1; i++) {
      dashedSegment(g, { x1: rubberBand[i]!.x, y1: rubberBand[i]!.y, x2: rubberBand[i + 1]!.x, y2: rubberBand[i + 1]!.y, dash: 6, gap: 4 });
    }
    g.stroke({ width: 2, color: stroke, alpha: 0.6, cap: "round" });
    container.addChild(g);
  }
  if (spec.polylinePreview) {
    const dots = new Graphics();
    for (const p of spec.polylinePreview) dots.circle(p.x, p.y, 3.5).fill({ color: stroke, alpha: 0.9 });
    container.addChild(dots);
  }
  return container;
}

/**
 * The Measure tool's ruler (`projects/measurement-tool.md` ruling 6), all in cells the engine
 * already counted (`measureBetween`): the line runs between the centres of the `nearest` pair, a pip
 * sits on each `counted` cell (so the pips number exactly the distance), each bound unit's footprint
 * is outlined, and the readout chip sits beside the `to` end.
 */
export type MeasureRenderSpec = {
  nearest: readonly [{ x: number; y: number }, { x: number; y: number }];
  counted: readonly { x: number; y: number }[];
  footprints: readonly (readonly { x: number; y: number }[])[];
  label: string;
  /** Another participant's slot id: their ruler draws in their cursor colour. Absent for this viewer's own. */
  by?: string;
};
/** The ruler's colour: a violet outside the band palette (move blue, boost blue, sensors teal, path
 *  white, attack red, halo cream) and apart from the four annotation strokes. Pinned by owed 6.1.
 *  Another participant's ruler takes their cursor colour instead. */
const MEASURE_COLOR = "#c49bff";
const MEASURE_WIDTH = 2.5;
const MEASURE_PIP_RADIUS = 3.5;

function buildMeasureContainer(spec: MeasureRenderSpec, board: { cols: number; rows: number }): Container {
  const container = new Container();
  const color = spec.by === undefined ? MEASURE_COLOR : cursorColor(spec.by);
  const centre = (c: { x: number; y: number }) => ({ x: (c.x + 0.5) * CELL, y: (c.y + 0.5) * CELL });
  for (const cells of spec.footprints) {
    const { loops } = bandGeometry(
      cells.map((c) => `${c.x},${c.y}`),
      board,
    );
    const outline = strokeTwice(
      new Graphics(),
      (g) => {
        for (const L of loops) g.poly(flat(L), true);
      },
      {
        under: { width: MEASURE_WIDTH + BAND_UNDER.grow, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha, join: "round" },
        top: { width: MEASURE_WIDTH, color, join: "round" },
      },
    );
    container.addChild(outline);
  }
  const a = centre(spec.nearest[0]);
  const b = centre(spec.nearest[1]);
  container.addChild(
    strokeTwice(new Graphics(), (g) => g.moveTo(a.x, a.y).lineTo(b.x, b.y), {
      under: { width: MEASURE_WIDTH + BAND_UNDER.grow, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha, cap: "round" },
      top: { width: MEASURE_WIDTH, color, cap: "round" },
    }),
  );
  const pips = new Graphics();
  for (const c of spec.counted) {
    const p = centre(c);
    pips.circle(p.x, p.y, MEASURE_PIP_RADIUS);
  }
  pips.fill(color).stroke({ width: 1.5, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha });
  container.addChild(pips);
  // The readout chip, right of the `to` end's cell: the cursor chip's shape in the ruler's colour.
  const { chip } = buildCursorChip(spec.label, color);
  chip.x = b.x + CELL / 2 + 4;
  chip.y = b.y - 9;
  container.addChild(chip);
  return container;
}

/** Build a cursor's name chip alone — the pill background + its `Text` label, tinted by the
 *  cursor's stable identity colour. Split out from {@link buildCursorContainer} so a label that
 *  resolves AFTER the reticle is already mounted (the cursor-event-before-
 *  slot-presence-broadcast race) can swap just this piece via {@link CursorSlot.chip}/`text`,
 *  instead of rebuilding the whole reticle mid-lerp. Returns the `Text` node alongside the chip
 *  `Container` so the caller can track it for a later swap AND (TEST-ONLY) so
 *  {@link CombatBoardHandle.debugCursorLabels} can read the LIVE rendered text straight off it. */
function buildCursorChip(label: string, color: string): { chip: Container; text: Text } {
  const fontSize = 11;
  const padL = 7;
  const tagW = label.length * fontSize * 0.62 + padL + 7;
  const tagH = fontSize + 7;
  const chip = new Container();
  chip.x = 18;
  chip.y = 16;
  chip.addChild(
    new Graphics()
      .poly([0, 0, tagW, 0, tagW, tagH, 6, tagH, 0, tagH - 6])
      .fill({ color: TOKEN_LABEL_BG, alpha: 0.82 })
      .stroke({ width: 1, color }),
  );
  const text = new Text({ text: label, style: new TextStyle({ fill: color, fontFamily: "JetBrains Mono, monospace", fontSize, fontWeight: "600" }) });
  text.anchor.set(0, 0.5);
  text.x = padL;
  text.y = tagH / 2;
  chip.addChild(text);
  return { chip, text };
}

/** One remote participant's cursor: an angular delta-pointer reticle + a name chip, both tinted by
 *  {@link cursorColor}. Position is LERPED and opacity FADED every tick (see the mount's ticker) —
 *  never prop-bound — so a throttled stream glides and a depart fades rather than popping/vanishing.
 *  `by` (the server-stamped slot id) drives IDENTITY — the tint via {@link cursorColor} and the key
 *  {@link CombatBoardHandle.setCursors} maps `cursorSlots` on — while `label` (the pre-resolved
 *  display name) drives the chip TEXT only. Keeping the two separate
 *  is deliberate: swapping the map key or the tint source to the name would change a cursor's colour
 *  the instant a name resolves, and would collapse two same-named participants into one cursor slot. */
function buildCursorContainer(by: string, label: string): { container: Container; chip: Container; text: Text } {
  const color = cursorColor(by);
  const c = new Container();
  c.alpha = 0;
  const tipFill = new Graphics().poly([0, 0, 18, 7.5, 7.5, 18]).fill({ color, alpha: 0.26 });
  const tipOutline = new Graphics().poly([0, 0, 18, 7.5, 7.5, 18]).stroke({ width: 1.6, color });
  const spine = new Graphics().moveTo(0, 0).lineTo(9, 9).stroke({ width: 1, color, alpha: 0.65 });
  const ring = new Graphics().circle(13, 13, 3.4).stroke({ width: 1, color, alpha: 0.8 });
  c.addChild(tipFill, tipOutline, spine, ring);

  const { chip, text } = buildCursorChip(label, color);
  c.addChild(chip);
  return { container: c, chip, text };
}

/** A live remote-cursor slot — the target the ticker lerps `container`'s position/opacity toward.
 *  `label` is the display name last rendered into `text` — tracked so {@link CombatBoardHandle.setCursors}
 *  can tell a genuine label change (rebuild the chip) from an unchanged one (leave it alone), the
 *  fix for the late-resolving-label race. */
type CursorSlot = { container: Container; target: { x: number; y: number }; alive: boolean; label: string; chip: Container; text: Text };

/** A live ping-burst instance — the ticker drives its expand/fade until it self-removes. */
type PingBurstEntry = { container: Container; startedAt: number };

/** A live floating-combat-text instance — the ticker rises + fades it until it self-removes (the
 *  Pixi re-home of the deleted `<DamageFloat>`). */
type FloatEntry = { node: Container; startedAt: number; startY: number; caption?: true };

/** A live impact-burst instance — the ticker expands + fades it until it self-removes (the Pixi
 *  re-home of the deleted `<ImpactBurst>`). The tween/lifecycle (scale + alpha over
 *  `IMPACT_BURST_MS`) is IDENTICAL for a ballistic and a tech burst — only the `container`'s own
 *  Graphics color differs, chosen once at `spawnImpact` time. */
type ImpactEntry = { container: Container; startedAt: number };

/** The token registration hooks `setTokens` drives on every rebuild — the Pixi analogue of
 *  "a token registers its node on mount, clears on unmount": since a
 *  Pixi token has no persistent per-token React component (`setTokens` rebuilds every container from
 *  scratch), the registration lives here, at the point the container is actually built/torn down.
 *  Wired from `usePresentationDirector`'s `registerNode`/`unregisterNode` (the animator's
 *  `NodeRegistry` bridge, `pixiAnimator.ts`). */
export type TokenNodeRegistry = { registerNode: (id: string, node: Container) => void; unregisterNode: (id: string) => void };

// ── The mount ──────────────────────────────────────────────────────────────────────────────────

export type CombatBoardView = { x: number; y: number; scale: number };

/** Resolved per-token render options `CombatStage.tsx` hands `setTokens` — everything domain-derived
 *  (selection, out-of-range gating) is resolved by the caller; this module only draws + tracks. */
export type TokenRenderOptions = {
  selectedId: string | null;
  untargetableIds: Set<string> | null;
  /** The viewing slot, for the "mine" ring (`controlsCombatant`, G-D7). Required-nullable per
   *  `decisions/0148`: `null` is the explicit "no viewer", which rings nothing. */
  viewerSlot: string | null;
  /** What each token's image resolves against — the roster, NPC roles and this viewer's
   *  disclosure (`lib/encounter/tokenImageCtx.ts`). */
  imageCtx: TokenImageCtx;
  /** Each token's turn state + vitals, already projected for this viewer (`tokenChromeFor`). A
   *  token with no entry draws none. The board reads turn state and vitals from here only, never
   *  off the raw combatant, which would leak a masked hostile's HP. */
  chrome: Record<string, TokenChrome>;
};

export type CombatBoardHandle = {
  app: Application;
  /** The pan/zoom root — exposed for the caller's own coordinate conversions if ever needed. */
  world: Container;
  /**
   * Re-read the host element's box and resize the renderer/canvas to match. The caller MUST call
   * this whenever the host resizes for a reason other than a window resize — Pixi's `resizeTo`
   * option only installs a `globalThis` `"resize"` listener (`ResizePlugin`), so a purely
   * layout-driven change to the host (COMMS docking, a window resize of the flex row) never reaches
   * the renderer on its own and leaves the canvas at its old size —
   * a dead gap of stage background where the board should be. `CombatStage.tsx`'s ResizeObserver is
   * the one caller.
   */
  resize(): void;
  setView(view: CombatBoardView): void;
  /**
   * Subscribe to the view once it holds still: `VIEW_SETTLE_MS` (`boardResolution.ts`) after the last `setView`, and
   * at once when the device pixel ratio changes. The payload is the world transform plus `dpr`, the
   * renderer resolution, so a layer that rasterizes wants `scale × dpr` device pixels per world
   * pixel. Board text re-rasterizes off this same signal. Returns the unsubscribe.
   */
  onViewSettled(listener: (view: SettledView) => void): () => void;
  /** TEST-ONLY: the renderer resolution, the page's `devicePixelRatio`, and the canvas backing
   *  store against its CSS box. With `autoDensity`, `backing` is `css × resolution`. */
  debugRenderResolution(): { resolution: number; dpr: number; backing: [number, number]; css: [number, number] };
  /** TEST-ONLY: the settled scale and its text level (1, 2 or 4), how long the render after the
   *  last re-rasterizing settle took, and a fresh count of every board text by its resolution
   *  (cursor chips excluded), so a text layer that skipped `sharpenText` shows up as a second key. */
  debugTextRaster(): { scale: number; level: number; lastMs: number; resolutions: Record<string, number> };
  setBoard(cols: number, rows: number): void;
  /** Terrain and edge walls, painted into chunk textures (`terrainArt/terrainLayer.ts`): a change
   *  repaints only the chunks it touches, and the old ones stay on screen until the new are ready. */
  setTerrain(terrain: TerrainMap, edges: EdgeMap, ground: EncounterGround): void;
  /** Show the faint full grid over the terrain (the PLAY view toggle, ruling 2). */
  setFullGrid(visible: boolean): void;
  /** TEST-ONLY: the terrain layer's last cold and incremental build ms, chunk count and texture
   *  bytes (`TerrainBuildStats`), for the budget flow. */
  debugTerrainBuild(): TerrainBuildStats;
  /** TEST-ONLY: whether the full grid layer is showing. */
  debugFullGrid(): boolean;
  /** Show the terrain mechanics overlay: each piece of same-mechanics terrain outlined and labelled
   *  (`terrain-mechanics-overlay`, the rail's VIEW toggle O). It follows `setTerrain` and `setBoard`. */
  setTerrainMechanics(on: boolean): void;
  /** The Inspect tool's hovered cell (`"x,y"`), or `null`: with the overlay off, only the pieces
   *  containing it are outlined and labelled. The overlay on shows every piece regardless. */
  setTerrainMechanicsFocus(cellKey: string | null): void;
  /** TEST-ONLY: whether the overlay is on, the Inspect focus cell, and each drawn piece with the label text it shows. */
  debugTerrainMechanics(): { on: boolean; focus: string | null; pieces: { label: string; compact: string; shown: string; cells: string[]; labelCell: string }[] };
  setRooms(rooms: Record<RoomId, Room>): void;
  /** Authored sitrep zones — render-only; the `hidden` flag
   *  DECORATES (dashed border + "GM ONLY"), it does not filter. The server's `projectSitrepFor`
   *  already stripped every zone this viewer isn't entitled to; see {@link buildSitrepZoneContainer}. */
  setSitrepZones(zones: SitrepZoneRender[]): void;
  /** Live `AreaEffect` clouds — one fill per record, rebuilt from
   *  scratch on every call (this file's default convention); a dispersed cloud's Graphics are torn
   *  down via `clearContainer`, never left orphaned on screen. */
  setAreaEffects(areaEffects: Record<AreaEffectId, AreaEffect>): void;
  /** Live drone auras (`droneAuraRings`): one dashed ring per field, rebuilt from scratch on every call. */
  setDroneAuras(rings: DroneAuraRing[]): void;
  setAuthoringPreview(specs: AuthoringPreviewSpec[]): void;
  /** Rebuild every placed combatant's token body + name label (§3 rows 9-10) from scratch. */
  setTokens(combatants: Combatant[], opts: TokenRenderOptions): void;
  /**
   * Move a token's container LIVE, in world px (top-left of its footprint) — the drag half of "the
   * renderer moves it live, state commits on drop". Called every
   * `mousemove` while `CombatStage.tsx`'s native-DOM drag is open; a no-op if `id` isn't currently
   * mounted (drag ended / token gone).
   */
  moveToken(id: string, topLeft: { x: number; y: number }): void;
  /** The single selected token the breathing halo ring (§3 row 8) tracks — ticker-driven transform/
   *  alpha only, geometry rebuilt just once per selection/token change (never per frame). */
  setSelection(id: string | null): void;
  /** The token the pointer is currently over, or `null`. Drives ONE thing: the hover half of the
   *  raised label layer (`syncLabelReveal`) — a hovered token's name is drawn above everything else,
   *  and a name the solver HID comes back at its anchor for as long as the pointer stays on the
   *  unit. Never a re-solve; nothing on the board moves.
   *
   *  `CombatStage.tsx`'s `onMouseMove` feeds it, from the same `combatCells.ts` `hitTestToken` math
   *  that drives select and drag (the ratified native-DOM interaction model — this module does not
   *  hit-test, and there is no Pixi `eventMode` anywhere in it). The caller coalesces to
   *  hover-CHANGE granularity; this is idempotent anyway. */
  setHoveredToken(id: string | null): void;
  setMoveRange(cells: Set<string>, cols: number, rows: number): void;
  /** The boost ring: cells reachable only with a Boost, beyond {@link setMoveRange}'s set, in the
   *  boost colour. Same layer shape as the move wash; an empty set clears it. */
  setBoostRange(cells: Set<string>, cols: number, rows: number): void;
  /** TEST-ONLY: how many child nodes the boost ring currently holds. */
  debugBoostRangeCount(): number;
  /** The Sensors (TECH-RANGE) reach wash: every cell within the
   *  attacker's frozen `base.sensors`, range-only (no LoS filter — mirrors the range-only
   *  `checkTechRange` wall). A dedicated, independently-clearable/idempotent layer — same shape as
   *  {@link setMoveRange}, NOT folded into {@link setTargeting}. Gated by the caller on any
   *  Sensors-scoped aim (the shipped `search` aim today, the tech aim modes once they join). */
  setSensorRange(cells: Set<string>, cols: number, rows: number): void;
  /** Hover intel's weapon rings (`projects/hover-intel.md`): one attack-range dashed edge per set,
   *  no wash, so nested rings and the move band under them stay legible, over ONE cover hatch
   *  underlay ({@link ARMED_COVER_HATCH}, `range-preview-cover`) drawn beneath every ring. An empty
   *  view clears it. */
  setThreatRings(view: ThreatRingsView, cols: number, rows: number): void;
  /** TEST-ONLY: how many rings {@link setThreatRings} last drew (a ring wholly off the board draws none). */
  debugThreatRingCount(): number;
  /** TEST-ONLY: the cell keys of each ring {@link setThreatRings} last drew, sorted. */
  debugThreatRingKeys(): string[][];
  /** TEST-ONLY: how many cover-hatch cells {@link setThreatRings} last drew under the rings. */
  debugThreatRingHatchCount(): number;
  /** The drag trail: `cells` in the path colour, `boostCells` (the part only a folded Boost pays for) in the boost colour. */
  setMovePath(path: MovePathPreview, cols: number, rows: number): void;
  /** Draw the next {@link setMovePath} as a refused trail (red): a Trampler's drag over the move left. */
  setMovePathRefused(refused: boolean): void;
  /** TEST-ONLY: whether the drawn move path is the refused (red) trail. */
  debugMovePathRefused(): boolean;
  setRemoteMovePaths(previews: { cells: string[]; by: string }[], cols: number, rows: number): void;
  /**
   * Create (or replace) the client-local PLAY-mode drag-ghost for `c.id` — a translucent copy of its
   * CURRENT look, positioned at `topLeft` (world px, footprint
   * top-left). Called lazily on the first real pointer travel of a drag (never on a stationary
   * click). Replaces any prior ghost for the same id outright (a fast re-drag before the previous
   * one's settle fires never leaves two ghosts stacked).
   */
  createGhost(c: Combatant, topLeft: { x: number; y: number }): void;
  /** Move an existing ghost's container LIVE, in world px — called every `mousemove` while a PLAY drag
   *  is open, mirroring {@link moveToken}'s live-drag contract but for the ghost, never the real node.
   *  A no-op if `id` has no ghost (drag never started, or already cleared). */
  moveGhost(id: string, topLeft: { x: number; y: number }): void;
  /** Snap a ghost to its settled drop-cell anchor on release (grid-aligned, not the raw last-sampled
   *  pointer position) — the caller then awaits the Director's `watchSettle` signal before tearing it
   *  down via {@link clearGhost}. A no-op if `id` has no ghost. */
  snapGhost(id: string, topLeft: { x: number; y: number }): void;
  /** Tear down (destroy) the drag-ghost for `id` — called on the Director's settle signal (arrival /
   *  server-adjusted arrival / removal), or immediately on a client-side reject with no walk to await.
   *  A no-op if `id` has no ghost. */
  clearGhost(id: string): void;
  /**
   * The **pending directed-deploy sections** (`jericho-directed-placement` D1 follow-up) — one translucent section
   * silhouette per cell the player has already picked in a live `activate_sites` multi-pick gesture,
   * so a section appears the moment its cell is committed instead of both appearing at once when the
   * single `request_activate` fires at the end (`jericho-directed-placement` D1 is unchanged — the
   * deploy is still ONE atomic resolution; this only closes the visible gap that created).
   *
   * **Wholesale replace, never incremental** — every call clears the layer and redraws from `cells`,
   * mirroring {@link setTargeting}/{@link setMovePath}'s own rebuild contract. That's what makes the
   * teardown total: `null` (or `[]`) clears the layer outright, so the caller's ONE effect covers
   * *every* exit path (commit, cancel, Esc, back-a-pick, turn change, unmount, socket drop) by
   * simply passing `null` — there is no per-path `clearPendingDeploy` to forget to call on the
   * seventh path someone adds later.
   *
   * `size` is the section footprint (`directedDeployRequirement().size`); out-of-bounds cells are
   * skipped exactly as {@link drawBand} skips them.
   */
  setPendingDeploy(cells: readonly string[] | null, opts: { size: number; cols: number; rows: number }): void;
  /** The attack reach + hovered AoE template (§3 row 7): per-cell cover hatches inside a dashed
   *  range boundary, plus the affected-cells band and LoS veils. Either of `cellSightlines`/`attackAffected` `null` clears its
   *  half independently. One options object, not 4 positional args — `CODE_STYLE.md`'s `max-params`
   *  gate caps at 3. */
  setTargeting(cellSightlines: CellSightline[] | null, opts: { attackAffected: Set<string> | null; attackLosExcluded: Set<string> | null; cols: number; rows: number }): void;
  setAnnotations(annotations: Record<AnnotationId, Annotation>, selectedId: AnnotationId | null): void;
  setDrawPreview(spec: DrawPreviewSpec | null): void;
  /** Every Measure ruler to draw ({@link MeasureRenderSpec}), later ones on top; `[]` clears them. */
  setMeasures(specs: readonly MeasureRenderSpec[], board: { cols: number; rows: number }): void;
  /** `by` is the slot id (identity/colour/map-key — never displayed); `label` is the pre-resolved
   *  display name the caller (`CombatApp`) already looked up via `useSlotNames` — this layer stays
   *  dumb and never resolves a name itself. */
  setCursors(cursors: { at: { x: number; y: number }; by: string; label: string }[]): void;
  /** Fire a transient ping burst at a world point, tinted by the pinger's stable cursor colour. */
  addPing(at: { x: number; y: number }, by: string): void;
  /** Fire a transient floating-combat-text item above a world point (the animator's `FloatSink`). */
  spawnFloat(item: { x: number; y: number; text: string; tone: FloatTone }): void;
  /** Ring these tokens as the chain link now playing (`chained-resolution-sequence` 2.2); `[]` clears.
   *  The ring follows each token's container, so it tracks a token mid-walk. */
  setFocus(ids: readonly string[]): void;
  /** Play an attack's travel in its family's look (the animator's `ProjectileSink`,
   *  `attack-animations` 3.1), or, for `clear`, drop every live attack look (a snap). */
  spawnProjectile(item: { attack: AttackTravelItem } | { clear: true }): void;
  /** Fire a transient impact burst at a world point (the animator's `ImpactSink`). `tech`
   *  recolors the SAME pooled burst hacker-green for a quick-tech flourish (Invade's hit, or a
   *  no-roll Scan/Lock On/Bolster) instead of spawning a second particle system — omitted/false is
   *  the ordinary ballistic orange fireball. */
  spawnImpact(item: { x: number; y: number; tech?: boolean } | { attack: AttackOutcomeItem }): void;
  /** TEST-ONLY: the attack looks played so far, oldest first (`attackFxLooks.ts`'s log), each with
   *  its `performance.now()` stamp — what `frontend/e2e/attack-fx.e2e.ts` reads. */
  debugAttackFxLog(): AttackFxLogEntry[];
  /** The current whole-board frame cap in fps (`0` = uncapped) — Pixi's native `Ticker.maxFPS`. */
  getMaxFPS(): number;
  /** Set the whole-board frame cap (fps); a non-positive value uncaps. Persists to `localStorage`
   *  so a dev's choice survives a reload. */
  setMaxFPS(fps: number): void;
  /** Independently-measured fps + renderer info, read by `frontend/e2e/combat-fps-cap.e2e.ts`. */
  getStats(): { measuredFps: number; maxFPS: number; frameCount: number; rendererKind: string };
  /** TEST-ONLY: how many child nodes the attack-reach overlay (§3 row 7) currently holds — `0` when
   *  nothing is armed / the overlay cleared, `≥1` once `setTargeting` has drawn a non-empty reach
   *  (the range band's nodes plus any hatches, template band and veils). A cheap non-visual read the
   *  attack-range render guard (`frontend/e2e/range-enforcement.e2e.ts`) asserts on, so a silent loss
   *  of the overlay fails a test instead of only a human eyeball. */
  debugAttackOverlayCount(): number;
  /** TEST-ONLY: how many live `AreaEffect` clouds are currently
   *  mounted — `0` when no cloud is live, `≥1` once `setAreaEffects` has drawn at least one. Same
   *  cheap non-visual-read shape as {@link debugAttackOverlayCount}; `frontend/e2e/smoke-area.e2e.ts`
   *  uses it to establish the positive window before asserting dispersal (a hollow-negative guard —
   *  see the worktree `CLAUDE.md`'s "Assertion races" section). */
  debugAreaEffectCount(): number;
  /** TEST-ONLY: how many drone-aura rings are drawn (`setDroneAuras`). */
  debugDroneAuraCount(): number;
  /** TEST-ONLY: how many child nodes the move-range wash (`setMoveRange`) currently holds — same
   *  non-visual-read shape as {@link debugAttackOverlayCount},
   *  the headless proof that the move band clears the instant an attack/tech aim arms
   *  (overlay-mode exclusivity) rather than stacking under the other band. NOT a per-cell count:
   *  a band is a few nodes (wash pieces, edge, pips), so read it as 0 / non-zero ("anything painted"). */
  debugMoveRangeCount(): number;
  /** TEST-ONLY: how many child nodes the Sensors (TECH-RANGE) wash (`setSensorRange`) currently holds —
   *  same shape as {@link debugMoveRangeCount}/{@link debugAttackOverlayCount}, the headless proof for
   *  the tech overlay's half of exclusivity. */
  debugSensorRangeCount(): number;
  /** TEST-ONLY: the drag-ghost's LIVE container position for `id`, or `undefined` if none exists right
   *  now — `frontend/e2e/ghost-drag.e2e.ts` samples this to assert the
   *  ghost renders during a PLAY drag, follows the pointer, snaps to the drop cell, and clears on
   *  settle, without scraping rendered pixels (mirrors {@link debugAttackOverlayCount}'s pattern). */
  debugGhostPos(id: string): { x: number; y: number } | undefined;
  /** TEST-ONLY: how many nodes the move-path band currently holds (`ghost-drag-movement` Phase
   *  1) — `0` when nothing is armed/cleared, `≥1` once `setMovePath` has drawn a non-empty preview.
   *  Same non-visual-read shape as {@link debugAttackOverlayCount}. */
  debugMovePathCount(): number;
  /** TEST-ONLY: how many cells the drawn trail shows in the boost colour (0 with no folded Boost). */
  debugMovePathBoostCells(): number;
  /** TEST-ONLY: how many pending directed-deploy section nodes are currently mounted
   *  (`jericho-directed-placement` D1 follow-up) — `0` when nothing is picked/the gesture ended, `N` after N picks.
   *  The seam a browser flow reads to assert a section appears at pick 1 (this is a canvas layer, so
   *  there is nothing in the DOM to query). Same non-visual-read shape as {@link debugMovePathCount}. */
  debugPendingDeployCount(): number;
  /** TEST-ONLY: every LIVE remote cursor's slot id (`by`) paired with its ACTUAL rendered chip
   *  text — reads straight off the mounted `Text.text`, not the
   *  data model, so a browser flow proves the PIXELS say a callsign rather than only proving the
   *  prop carried one (this repo has shipped canvas bugs behind a green headless/prop-only test
   *  before). `[]` when no remote cursor is live. Same non-visual-read shape as
   *  {@link debugAttackOverlayCount}. */
  debugCursorLabels(): { by: string; label: string }[];
  /** TEST-ONLY: every LIVE token's world-space bbox, keyed by combatant id — `{x,y,w,h}` off
   *  `tokenEntries`' container position + `side`, padded by the drawn hex's overflow
   *  (`tokenOverflow`): the SAME numbers `labelLayout.ts`'s solver consumes as its `token` rects. World space, not screen space: `labelsContainer`/
   *  `tokensContainer` are siblings, direct children of `world`, so no extra transform sits between
   *  a container's local `.x`/`.y` and this rect — which is why world space is the right frame for
   *  the solver. Same non-visual-read shape as {@link debugAttackOverlayCount}. */
  debugTokenBounds(): Record<string, { x: number; y: number; w: number; h: number }>;
  /** TEST-ONLY: every LIVE label's world-space bbox PLUS its actual rendered `Text.text`, keyed by
   *  combatant id — the bbox is read straight off the mounted
   *  `Text`'s real `.width`/`.height` (not the wrap-width style), so a browser flow measures the
   *  PIXELS, not just the layout intent; `text` rides along on the same live read (mirrors
   *  {@link debugCursorLabels}' "read the pixels, not the data model" pattern) so a flow can prove
   *  the rendered `(n/m)` instance suffix survives without a second getter. The
   *  `label-layout.e2e.ts` red witness (today's static under-token layout, pre-Phase-2) diffs the
   *  bboxes against {@link debugTokenBounds} and against each other to prove the overlap the
   *  tracker predicted. Same non-visual-read shape as {@link debugAttackOverlayCount}. */
  debugLabelBounds(): Record<string, { x: number; y: number; w: number; h: number; text: string }>;
  /** TEST-ONLY: every LIVE label's placement KIND (`labelLayout.ts`'s `PlacedWhere`) and whether its
   *  pill is actually on screen, keyed by combatant id. The companion {@link debugLabelBounds}
   *  needs: that accessor reports a rect only for a DRAWN pill, so a hidden label is simply absent
   *  from it — which is correct (it occupies no pixels) but indistinguishable from "this token has
   *  no label at all". This says which of the two it is, and it is the read
   *  `frontend/e2e/label-layout.e2e.ts` uses to assert a label is hidden rather than merely missing.
   *  `drawn` is `bg.visible`, the same fact `debugLabelBounds` gates on, so the two can never
   *  disagree; `revealed` is whether the label is currently in the raised hover/selection layer, so
   *  a flow can tell a pill that is drawn because it was PLACED from one that is drawn because the
   *  pointer is on its token. Same non-visual-read shape as {@link debugAttackOverlayCount}. */
  debugLabelStates(): Record<string, { where: PlacedWhere; drawn: boolean; revealed: boolean }>;
  /** TEST-ONLY: a cell's CENTER in canvas-relative CSS pixels, keyed `"x,y"` like every other
   *  key-addressed read here — the cell-space twin of `getTokenScreenPos`.
   *
   *  It exists because a flow that wants to HOVER a known square has no token to aim at: the pane
   *  under test describes ground, and the interesting cells (a cover-and-difficult tile, an empty
   *  one) are deliberately empty. Reconstructing the point from the stage's pan/zoom in the flow is
   *  the thing `getTokenScreenPos`' own doc warns against — the centering formula silently
   *  mis-locates whenever the canvas isn't the full wrap height, which EDIT's authoring rail causes.
   *  So the transform is done HERE, through `world.toGlobal`, the same container the tokens hang
   *  off, and the flow just moves the mouse where it is told.
   *
   *  `undefined` for a malformed key. Off-grid coordinates still return a point — bounds are the
   *  caller's business, and a flow may legitimately want to aim just outside the board (the pane
   *  under test reports `null` there, which is itself worth asserting). */
  debugCellScreenPos(key: string): { x: number; y: number } | undefined;
  /**
   * Ring the units an action could legally target (`projects/token-command-menu.md` 2.4/2.5): a
   * hex ring per id in the kind's {@link TARGETING_RGB} colour, pulsing (scale and fade) with
   * the `hover` or the faster `armed` period, or still when `pulse` is `false` (reduced motion).
   * With `spin`, a ring whose id the previous call did not ring spins in once first (a 120° turn,
   * scaling up and fading in); an id that stays ringed across calls (a hover → armed switch) does
   * not spin again. A separate layer under the tokens, following each token's live position; token
   * drawing and `tokenStyleKey` are untouched. An empty list clears it. Wholesale replace on every call.
   */
  setTargetRings(ids: readonly string[], opts: { kind: TargetingKind; phase: "hover" | "armed"; pulse: boolean; spin: boolean }): void;
  /** TEST-ONLY: the ids currently ringed by {@link setTargetRings}. */
  debugTargetRingIds(): string[];
  /** TEST-ONLY: the last {@link setTargetRings} phase, and each ringed id's intro start (`performance.now()` ms, `null` for a ring that did not spin in). */
  debugTargetRingSpin(): { phase: "hover" | "armed"; bornAt: Record<string, number | null> };
  /** TEST-ONLY: the annotation ids {@link setAnnotations} last drew. */
  debugAnnotationIds(): string[];
  /**
   * The armed attack's mini forecast badges (`projects/target-mini-forecasts.md` 1.3), one per id,
   * wholesale replaced. Placed through `targetBadgeLayout.ts` against every token, every drawn name
   * label and the other badges; re-placed when {@link setTokens} moves a token or a label, never per
   * frame. An empty map clears them.
   *
   * `expandedId` is the target whose full forecast card is open: its badge hides while the card
   * shows, and comes back when it closes. Changing only `expandedId` re-places nothing.
   */
  setTargetBadges(badges: ReadonlyMap<string, TargetBadgeSpec>, opts: { expandedId: string | null }): void;
  /** TEST-ONLY: every badge, with its drawn text, its world-space rect and whether it is on screen. */
  debugTargetBadges(): Record<string, { text: string; x: number; y: number; w: number; h: number; where: PlacedWhere; visible: boolean }>;
  /** TEST-ONLY: the token ids drawn with the "mine" ring (the viewer controls them, G-D7). */
  debugMineTokenIds(): string[];
  /** TEST-ONLY: each placed token's resolved art — its `tokenImageFor` tier, the texture URL it
   *  requested (`null` for a generic ring), and whether that texture has decoded onto the board. */
  debugTokenArt(): Record<string, TokenArtState>;
  /** TEST-ONLY: the {@link TokenChrome} each placed token was last drawn with (absent when the
   *  caller passed none for it). This is what the board shows, per viewer: a masked hostile reads
   *  `hp: null` here exactly when no HP bar is drawn. */
  debugTokenChrome(): Record<string, TokenChrome>;
  /** TEST-ONLY: the token the active-unit beacon rings and whether it pulses (not under reduced motion), or `null`. */
  debugActiveBeacon(): { id: string; pulse: boolean } | null;
  /** TEST-ONLY: the world-space rect of each token's chrome row above its footprint (a size-1
   *  unit's status letters) — the obstacle `setTokens` hands the label
   *  solver. Absent for a token whose chrome stays inside its footprint. */
  debugChromeRowBounds(): Record<string, { x: number; y: number; w: number; h: number }>;
  destroy(): void;
};

/**
 * Mount the combat board into `host` (an empty block element the caller owns — one `useEffect`
 * calls this one function). Returns a handle of imperative sync functions; the effect's cleanup
 * should call `destroy()`. `nodeRegistry` wires `setTokens`' register/unregister calls to the
 * animator's `NodeRegistry` (see {@link TokenNodeRegistry}).
 */
export async function mountCombatBoard(host: HTMLDivElement, nodeRegistry: TokenNodeRegistry): Promise<CombatBoardHandle> {
  // One real renderer/WebGL-context/shader build — bump the persistent, unmount-surviving counter
  // BEFORE constructing it, so a flow reading the counter mid-`await app.init()` still
  // observes the attempt.
  combatBoardBuildCount += 1;
  const app = new Application();
  await app.init({
    // Explicit `webgl` preference, not `autoDetectRenderer`'s default order: a sandboxed
    // headless-Chrome test runner reports the WebGPU API surface present but then fails the real
    // adapter request, silently falling all the way through to a Canvas2D fallback if left to
    // auto-detect.
    preference: "webgl",
    background: STAGE_BG,
    resizeTo: host,
    antialias: true,
    // Both are needed: `autoDensity` alone is a no-op at Pixi's default resolution of 1, which
    // left a retina canvas at half its pixels and the browser upscaling it (`projects/crisp-zoom.md`).
    resolution: boardResolution(window.devicePixelRatio),
    autoDensity: true,
  });
  host.appendChild(app.canvas);
  // Native frame-rate cap (see the module header) — a dev's chosen cap (via `setMaxFPS` below)
  // persists to `localStorage` so it survives a reload.
  app.ticker.maxFPS = readInitialMaxFps();
  const rendererKind = app.renderer.type === RendererType.WEBGPU ? "webgpu" : app.renderer.type === RendererType.WEBGL ? "webgl" : "canvas";

  const world = new Container();
  app.stage.addChild(world);

  // Z-order bottom → top, originally(1-15; row 8
  // `sensorRangeContainer` joined in, pushing halo/tokens/etc down
  // one, so the doc's literal row numbers are now historical — the list below is the live source of
  // truth). A Pixi Container is a free scene-graph node (§3's callout on the per-canvas cost this
  // migration retires), so every container below is added unconditionally — no "only mount while
  // non-empty" bookkeeping needed.
  const boardContainer = new Container(); // 1
  // The terrain art is opaque over the whole board, so everything that used to show through it
  // sits above it: the PLAY full grid, then rooms (`terrain-tile-art` 4.5).
  const terrainContainer = new Container(); // 2
  const assetsContainer = new Container(); // 2.1 (map assets: trees and set pieces, `terrainArt/assetLayer.ts`)
  const fullGridContainer = new Container(); // 2.5
  fullGridContainer.visible = false;
  const roomsContainer = new Container(); // 3
  // Live `AreaEffect` clouds — sits just above terrain (a cloud is a
  // battlefield layer like terrain, not a per-frame VFX) and below every dot/wash overlay + tokens, so
  // a unit standing "in" the cloud still renders on top of its low-alpha fill.
  const areaEffectsContainer = new Container(); // 3.5
  const droneAurasContainer = new Container(); // 3.55 (drone aura rings, just above the clouds)
  let droneAuraCount = 0;
  // Authored sitrep zones — above terrain + clouds (an objective
  // region must read over whatever the GM painted) and below every targeting overlay and token, so a
  // unit standing in the landing zone still renders on top of its wash.
  const sitrepZonesContainer = new Container(); // 3.6
  // The terrain mechanics overlay: outlines, then their labels, over the ground and zones and under
  // every targeting band and token, so an aim's bands always win (`terrain-mechanics-overlay` ruling 6).
  const terrainMechanicsContainer = new Container(); // 3.7
  const terrainMechanicsLabelsContainer = new Container(); // 3.8
  const moveRangeContainer = new Container(); // 4
  const boostRangeContainer = new Container(); // 4.1 (the boost ring, beside the move wash)
  // The boost band edges the move range and the boost cells together, so it redraws when either changes.
  let moveRangeCells: Set<string> = new Set();
  let boostRange: { cells: Set<string>; cols: number; rows: number } | null = null;
  function drawBoostBand(): void {
    clearContainer(boostRangeContainer);
    if (!boostRange || boostRange.cells.size === 0) return;
    const { cells, cols, rows } = boostRange;
    drawBand(boostRangeContainer, cells, { cols, rows, style: BOOST_RANGE_BAND, edgeKeys: [...moveRangeCells, ...cells] });
  }
  const movePathContainer = new Container(); // 5
  let movePathRefused = false;
  let movePathBoostCells = 0;
  const remoteMovePathContainer = new Container(); // 6
  const attackOverlayContainer = new Container(); // 7
  // The Sensors (TECH-RANGE) reach wash — a NEW, dedicated, independently-clearable layer (mirrors
  // `moveRangeContainer`'s pattern), NOT folded into `attackOverlayContainer` (which owns weapon-red /
  // cover-hatch / AoE semantics). Gated on any Sensors-scoped aim; see `setSensorRange`.
  const sensorRangeContainer = new Container(); // 8
  // Hover intel's weapon rings (`setThreatRings`), beside the attack overlay they share a style with.
  const threatRingContainer = new Container(); // 8.5
  let threatRingCount = 0;
  let threatRingKeys: string[][] = [];
  let threatRingHatchCount = 0;
  const haloContainer = new Container(); // 9
  // The active unit's beacon ring (`rebuildBeacon`), beside the halo, under the tokens.
  const beaconContainer = new Container(); // 9.2
  // The command menu's target rings (`setTargetRings`) — beside the halo, under the tokens.
  const targetRingContainer = new Container(); // 9.5
  const tokensContainer = new Container(); // 10
  const labelsContainer = new Container(); // 11
  // The armed attack's mini forecast badges (`setTargetBadges`): above the name labels they are
  // placed around, below the raised hover/selection label layer.
  const targetBadgeContainer = new Container(); // 11.05
  // The RAISED label layer (Phase 3.2 of `projects/crowded-label-placement.md`) — normally empty. A
  // token that is HOVERED or SELECTED has its label reparented here for as long as that lasts, so it
  // draws above every other token and label instead of under whatever happens to be stacked on it.
  // It is a display-list move, NOT a re-solve: the label keeps the exact `dx`/`dy` the solver gave it
  // (or its anchor, if it was hidden), so nothing on the board shifts when the pointer passes over a
  // unit. Sibling of `labelsContainer` under the same untransformed `world`, which is what keeps
  // `debugLabelBounds`' world-space rects identical either side of the move.
  const labelRevealContainer = new Container(); // 11.1
  // The PLAY-mode drag-ghost overlay — a client-local translucent
  // copy of the dragged token, never broadcast. Sits above the real tokens/labels/halo (so it reads
  // clearly even where it overlaps a real token) and below the persisted annotations/VFX/cursor layers.
  const dragGhostContainer = new Container(); // 11.5
  // Pending directed-deploy sections (`jericho-directed-placement` D1 follow-up) — the cells the player has already
  // clicked during an `activate_sites` multi-pick gesture, drawn as translucent section silhouettes
  // so each one appears AS IT IS COMMITTED rather than all at once when the single `request_activate`
  // finally fires (`jericho-directed-placement` D1 keeps the deploy one atomic resolution; this layer
  // closes the *visible* gap that atomicity opened, without touching authority).
  //
  // Its own layer, sibling to the drag ghost and for the same reason: both are client-local, never
  // broadcast, and must read clearly where they overlap the real board. A pure `Graphics` overlay —
  // it is NEVER registered in `tokenEntries`, so it cannot be selected, dragged, targeted, hit-tested,
  // or read by the halo/label reconcile, and it contributes nothing to LoS/cover (which the SERVER
  // derives from `state.combatants` — a render-layer node is invisible to it by construction).
  const pendingDeployContainer = new Container(); // 11.6
  const annotationsContainer = new Container(); // 12
  const drawPreviewContainer = new Container(); // 13
  const measureContainer = new Container(); // 13.5 (the Measure ruler, client-local)
  const vfxContainer = new Container(); // 14 (ping bursts)
  /** The attack looks' ground marks (a lobbed shell's shadow, a crater, a beam's scorch), under the tokens. */
  const attackFxUnderContainer = new Container();
  const authoringPreviewContainer = new Container(); // 15
  const cursorsContainer = new Container(); // 16
  world.addChild(
    boardContainer,
    terrainContainer,
    assetsContainer,
    fullGridContainer,
    roomsContainer,
    areaEffectsContainer,
    droneAurasContainer,
    sitrepZonesContainer,
    terrainMechanicsContainer,
    terrainMechanicsLabelsContainer,
    moveRangeContainer,
    boostRangeContainer,
    movePathContainer,
    remoteMovePathContainer,
    attackOverlayContainer,
    sensorRangeContainer,
    threatRingContainer,
    haloContainer,
    beaconContainer,
    targetRingContainer,
    attackFxUnderContainer,
    tokensContainer,
    labelsContainer,
    targetBadgeContainer,
    labelRevealContainer,
    dragGhostContainer,
    pendingDeployContainer,
    annotationsContainer,
    drawPreviewContainer,
    measureContainer,
    vfxContainer,
    authoringPreviewContainer,
    cursorsContainer,
  );
  // Before the shared ticker below, which follows each asset's token every frame.
  const assetLayer = createAssetLayer({ container: assetsContainer });

  /** The live cloud fills currently mounted in `areaEffectsContainer` — reassigned wholesale by
   *  `setAreaEffects` on every rebuild; the shared ticker below reads this array each frame to drive
   *  the breathing alpha (transform/alpha only, never a rebuild — see `AREA_EFFECT_BREATH_*`'s doc). */
  let areaEffectFills: Graphics[] = [];

  // ── Token registry + the selection halo (§3 rows 8-10) ──────────────────────────────────────────
  // `tokenEntries` is this module's own live-position source of truth. The halo/labels below track a
  // token's LIVE position by reading straight off `tokenEntries` (which `moveToken` also mutates
  // during a local drag) — no Director/registry indirection needed for either; `nodeRegistry` (Phase
  // 4) is a SEPARATE registration `pixiAnimator.ts` reads to tween a token's container by id.
  // `setTokens` reconciles both maps by combatant id: a persisting id keeps its EXISTING
  // container/registry entry untouched (so an in-flight tween/drag survives the call); only genuinely
  // added/removed ids call `registerNode`/`unregisterNode`.
  const tokenEntries = new Map<string, TokenEntry>();
  const labelEntries = new Map<string, LabelEntry>();
  /** Live drag-ghosts by combatant id — keyed so multiple concurrent
   *  drags (or a fast re-drag of the same token before its prior settle fires) never fight over one
   *  shared container. Built via {@link renderTokenLook} (the SAME look-rendering as a real token) so
   *  the ghost reads as an actual translucent copy, not a placeholder shape. */
  const ghostEntries = new Map<string, Container>();
  /** The image ctx from the last `setTokens`, so a drag ghost shows the same art as its token. */
  let lastImageCtx: TokenImageCtx | null = null;
  let selectedTokenId: string | null = null;
  /** The token the pointer is over, fed in by `CombatStage.tsx`'s `onMouseMove` through
   *  {@link CombatBoardHandle.setHoveredToken} — the SAME native-DOM + pure-math `hitTestToken`
   *  hit-test that drives select and drag, not a second one and not Pixi `eventMode` interactivity
   *  (see this module's header). Its only consumer is {@link syncLabelReveal}. */
  let hoveredTokenId: string | null = null;
  const haloRing = new Graphics();
  const haloGlow = new Graphics();
  haloGlow.filters = [new BlurFilter({ strength: 4 })];
  haloRing.visible = false;
  haloGlow.visible = false;
  haloContainer.addChild(haloGlow, haloRing);
  const beaconRing = new Graphics();
  beaconRing.visible = false;
  beaconContainer.addChild(beaconRing);
  /** The token the beacon rings (`activeBeaconId` over the tokens' chrome), and whether it pulses. */
  let beacon: { id: string; pulse: boolean } | null = null;
  /** {@link CombatBoardHandle.setTargetRings}' live rings and their pulse. */
  const targetRings = new Map<string, Graphics>();
  /** When each ringed id's spin-in intro started, or `null` for no intro; survives the wholesale replace. */
  let targetRingBornAt = new Map<string, number | null>();
  let targetRingPhase: "hover" | "armed" = "hover";
  const reducedMotion = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  // The annotation ids last drawn (`debugAnnotationIds`).
  let drawnAnnotationIds: string[] = [];
  let targetRingPulse: { periodMs: number } | null = null;
  /** {@link CombatBoardHandle.setTargetBadges}' live badges, the content key they were built from,
   *  and the one whose full card is open. */
  const badgeEntries = new Map<string, BadgeEntry>();
  let badgeKey = "";
  let expandedBadgeId: string | null = null;

  /** A token's resting footprint rect, the frame every label and badge offset is relative to. */
  function restRect(entry: TokenEntry): Rect {
    return { x: entry.restMinX * CELL, y: entry.restMinY * CELL, w: entry.side, h: entry.side };
  }

  /**
   * Place every badge (`targetBadgeLayout.ts`) against the current tokens and drawn name labels.
   * Called from `setTargetBadges` on a content change and from `setTokens`' tail after the name
   * labels are solved, so a badge never sits where a label or token has just moved to.
   */
  function solveBadges(): void {
    if (badgeEntries.size === 0) return;
    // Every token as DRAWN, its hex overflowing the footprint (`tokenOverflow`).
    const tokens = [...tokenEntries.values()].map((e) => {
      const over = tokenOverflow(e.side, CELL);
      const r = restRect(e);
      return { x: r.x - over.x, y: r.y - over.y, w: r.w + 2 * over.x, h: r.h + 2 * over.y };
    });
    const labels: Rect[] = [];
    for (const [id, l] of labelEntries) {
      const t = tokenEntries.get(id);
      if (t && l.where !== "hidden") labels.push({ x: t.restMinX * CELL + l.dx, y: t.restMinY * CELL + l.dy, w: l.w, h: l.h });
    }
    const badges = [];
    for (const [id, b] of badgeEntries) {
      const t = tokenEntries.get(id);
      if (t) badges.push({ id, token: restRect(t), badge: { w: b.w, h: b.h }, ...(b.slot !== undefined ? { prevSlot: b.slot } : {}) });
    }
    // Token decorations outside a footprint would join here, the same list the name-label solve gets
    // (see that solve's `obstacles` note in `setTokens`); there are none yet.
    const placed = placeTargetBadges({ badges, tokens, labels });
    for (const [id, b] of badgeEntries) {
      const p = placed.get(id);
      b.where = p?.where ?? "hidden";
      b.slot = p?.slot;
      b.dx = p?.dx ?? 0;
      b.dy = p?.dy ?? 0;
    }
    syncBadgeVisibility();
  }

  /** A badge is on screen iff it was placed, its target is on the board, and its full card is not open. */
  function syncBadgeVisibility(): void {
    for (const [id, b] of badgeEntries) b.container.visible = b.where !== "hidden" && tokenEntries.has(id) && id !== expandedBadgeId;
  }

  /** Rebuild the halo ring's GEOMETRY (radius/color) — called only on a selection or token-set change,
   *  never per frame — the ticker below only ever mutates transform/alpha on the built geometry. */
  function rebuildHalo(): void {
    haloRing.clear();
    haloGlow.clear();
    const entry = selectedTokenId ? tokenEntries.get(selectedTokenId) : undefined;
    if (!entry) {
      haloRing.visible = false;
      haloGlow.visible = false;
      return;
    }
    // The cream halo (ruling 6) on a dark under-ring, a hex like the token; the glow beneath keeps
    // the side's colour.
    const hex = hexPoints(0, 0, markRingRadius(entry.side, CELL));
    strokeTwice(haloRing, (g) => g.poly(hex, true), {
      under: { width: HALO_WIDTH + 3.5, color: BAND_UNDER.color, alpha: 0.7 },
      top: { width: HALO_WIDTH, color: HALO_CREAM },
    });
    haloGlow.poly(hex, true).stroke({ width: 5, color: entry.accent, alpha: 0.6 });
    haloRing.visible = true;
    haloGlow.visible = true;
  }

  /** Rebuild the beacon ring's GEOMETRY for the token whose chrome reads ACTIVE — on a token-set
   *  change only; the ticker pulses it and follows the token, like the halo. */
  function rebuildBeacon(): void {
    beaconRing.clear();
    const id = activeBeaconId([...tokenEntries].map(([tid, e]) => [tid, e.chrome] as const));
    const entry = id !== null ? tokenEntries.get(id) : undefined;
    if (id === null || !entry) {
      beacon = null;
      beaconRing.visible = false;
      return;
    }
    const hex = hexPoints(0, 0, activeBeaconRadius({ side: entry.side, cell: CELL }));
    strokeTwice(beaconRing, (g) => g.poly(hex, true), {
      under: { width: BEACON_WIDTH + 3, color: BAND_UNDER.color, alpha: 0.7 },
      top: { width: BEACON_WIDTH, color: STATE_COLOR },
    });
    beacon = { id, pulse: !prefersReducedMotion() };
    beaconRing.alpha = 1;
    beaconRing.visible = true;
  }

  /**
   * Put every label in the right layer and the right visibility state for the CURRENT hover +
   * selection. The one place either fact is turned into pixels, called from `setTokens`' tail,
   * `setSelection` and `setHoveredToken` — so there is exactly one rule and no second source of
   * truth for "is this token selected" (that stays `selectedTokenId`, the same field `rebuildHalo`
   * reads).
   *
   * Two things happen to a revealed label, and neither is a re-solve:
   *   - it moves into {@link labelRevealContainer}, so it draws above every other token and label;
   *   - if the solver HID it, its pill and text come back on at the label anchor (`dx`/`dy` is
   *     candidate 0 for a hidden entry) and the tick stands down. Leaving hover / deselecting puts
   *     both back exactly as they were.
   *
   * A label's `dx`/`dy` never changes here, so the solved layout is untouched and the board does not
   * shift as the pointer crosses a unit. `addChild` on an existing child MOVES it (Pixi doesn't
   * duplicate), so the parent comparison is only there to avoid re-appending every call.
   */
  function syncLabelReveal(): void {
    for (const [id, entry] of labelEntries) {
      const revealed = id === hoveredTokenId || id === selectedTokenId;
      const parent = revealed ? labelRevealContainer : labelsContainer;
      if (entry.container.parent !== parent) parent.addChild(entry.container);
      const drawn = revealed || (!entry.onReveal && entry.where !== "hidden");
      entry.bg.visible = drawn;
      entry.text.visible = drawn;
      // The tick geometry only exists for a solver-hidden entry (`setTokens` clears it otherwise), so
      // this is exactly "hidden, and not currently revealed".
      entry.tick.visible = !drawn && !entry.onReveal;
    }
  }

  const cursorSlots = new Map<string, CursorSlot>();
  const pingBursts: PingBurstEntry[] = [];
  // The animator's float/projectile/impact VFX pools — the
  // Pixi re-home of the deleted `<DamageFloat>`/`<ProjectileFlourish>`/`<ImpactBurst>` React
  // overlays, driven by the SAME shared ticker below (self-managing, no React state involved).
  const floatEntries: FloatEntry[] = [];
  // The chain link's focus ring: one Graphics redrawn each frame around the focused tokens' live
  // containers (so it tracks a walk), empty when nothing is in focus.
  let focusIds: readonly string[] = [];
  const focusRing = new Graphics();
  vfxContainer.addChild(focusRing);
  // The attack looks (`attack-animations` 3.1): one scene over the VFX pool, with ground marks
  // (shadows, craters, scorch) on a layer just under the tokens.
  const attackFxScene = new FxScene({ over: vfxContainer, under: attackFxUnderContainer });
  const attackFx = createAttackFxLooks(attackFxScene, CELL);
  const impactEntries: ImpactEntry[] = [];

  // FPS measurement, independent of Pixi's own `ticker.FPS` self-report — a rolling frame counter
  // over ~500ms windows using wall-clock time — this is what proves `maxFPS` genuinely throttles
  // the rate rather than merely that the field is set. Read via `getStats()`.
  let fpsWindowFrames = 0;
  let fpsWindowStart = performance.now();
  let measuredFps = 0;
  let totalFrames = 0;

  // ── The one shared ticker — labels-follow, the halo pulse, cursor lerp/fade, ping-burst progress.
  // Every branch here only WRITES transform/alpha/text-position on already-built geometry (or removes
  // a finished burst/settled cursor); nothing rebuilds a shape per frame.
  app.ticker.add((ticker) => {
    totalFrames++;
    fpsWindowFrames++;
    const nowMs = performance.now();
    const elapsedMs = nowMs - fpsWindowStart;
    if (elapsedMs >= 500) {
      measuredFps = (fpsWindowFrames * 1000) / elapsedMs;
      fpsWindowFrames = 0;
      fpsWindowStart = nowMs;
    }

    // Labels-follow: reposition every label off its token's LIVE container position, re-applying the
    // label's SOLVED offset (`dx`/`dy`, from `setTokens`' tail — see `LabelEntry`'s doc), never
    // recomputed here. Covers a local drag (this client's own `moveToken` calls move the container
    // ahead of any committed state) at zero extra cost — a token that isn't moving just gets the same
    // x/y written back every frame.
    for (const [id, entry] of tokenEntries) {
      assetLayer.follow(id, { x: entry.container.x, y: entry.container.y, alpha: entry.container.alpha });
      const labelEntry = labelEntries.get(id);
      if (labelEntry) {
        labelEntry.container.x = entry.container.x + labelEntry.dx;
        labelEntry.container.y = entry.container.y + labelEntry.dy;
      }
      const badge = badgeEntries.get(id);
      if (badge) {
        badge.container.x = entry.container.x + badge.dx;
        badge.container.y = entry.container.y + badge.dy;
      }
    }

    // The READY peak marks glow during their side's pick: a slow breath, or steady under reduced motion.
    const peakPhase = (Math.sin((performance.now() / PEAK_GLOW_PERIOD_MS) * 2 * Math.PI) + 1) / 2;
    const peakAlpha = reducedMotion?.matches ? PEAK_GLOW_STILL : PEAK_GLOW_MIN + (1 - PEAK_GLOW_MIN) * peakPhase;
    for (const entry of tokenEntries.values()) {
      const glow = entry.container.getChildByLabel("peak-glow");
      if (glow) glow.alpha = peakAlpha;
    }

    if (targetRings.size > 0) {
      // Alternate 1 → 1.16 scale and 1 → 0.4 alpha over one period, then back (the prototype's
      // `tgtRing ... infinite alternate`); a still ring when the pulse is off.
      const now = performance.now();
      for (const [id, ring] of targetRings) {
        const entry = tokenEntries.get(id);
        ring.visible = entry !== undefined;
        if (!entry) continue;
        ring.x = entry.container.x + entry.side / 2;
        ring.y = entry.container.y + entry.side / 2;
        const bornAt = targetRingBornAt.get(id) ?? null;
        const intro = bornAt === null ? 1 : Math.min(1, (now - bornAt) / TARGET_RING_INTRO_MS);
        if (intro < 1) {
          // The spin-in, eased out: turn into place while growing and fading in.
          const e = 1 - (1 - intro) ** 3;
          ring.rotation = -(1 - e) * TARGET_RING_INTRO_TURN;
          ring.scale.set(TARGET_RING_INTRO_SCALE + (1 - TARGET_RING_INTRO_SCALE) * e);
          ring.alpha = e;
          continue;
        }
        ring.rotation = 0;
        // A spun ring's pulse starts from rest when its intro ends, so the hand-off never jumps.
        const t = bornAt === null ? now : now - bornAt - TARGET_RING_INTRO_MS;
        const phase = targetRingPulse ? 1 - Math.abs(((t / targetRingPulse.periodMs) % 2) - 1) : 0;
        ring.scale.set(1 + phase * 0.16);
        ring.alpha = 1 - phase * 0.6;
      }
    }

    if (selectedTokenId) {
      const entry = tokenEntries.get(selectedTokenId);
      if (entry) {
        const t = performance.now() / 1000;
        const phase = (Math.sin(t * ((2 * Math.PI) / HALO_PERIOD_S)) + 1) / 2; // 0..1
        haloRing.x = haloGlow.x = entry.container.x + entry.side / 2;
        haloRing.y = haloGlow.y = entry.container.y + entry.side / 2;
        haloGlow.alpha = 0.35 + phase * 0.5;
        haloRing.alpha = HALO_RING_MIN_ALPHA + phase * (1 - HALO_RING_MIN_ALPHA);
        const scale = 1 + phase * 0.12;
        haloRing.scale.set(scale);
        haloGlow.scale.set(scale * 1.15);
      }
    }

    if (beacon) {
      const entry = tokenEntries.get(beacon.id);
      if (entry) {
        beaconRing.x = entry.container.x + entry.side / 2;
        beaconRing.y = entry.container.y + entry.side / 2;
        if (beacon.pulse) {
          const phase = (Math.sin((performance.now() / 1000) * ((2 * Math.PI) / BEACON_PERIOD_S)) + 1) / 2;
          beaconRing.alpha = BEACON_MIN_ALPHA + phase * (1 - BEACON_MIN_ALPHA);
        }
      }
    }

    // Remote cursors: exponential-smoothing lerp toward the latest target + a fade toward alive?1:0,
    // frame-rate independent via `deltaMS`. Counter-scaled by the inverse world zoom so the reticle
    // stays constant-size on screen regardless of the viewer's pan/zoom.
    if (cursorSlots.size > 0) {
      const dt = ticker.deltaMS;
      const invScale = 1 / (world.scale.x || 1);
      for (const [by, slot] of cursorSlots) {
        const k = 1 - Math.exp(-dt / CURSOR_LERP_TAU_MS);
        slot.container.x += (slot.target.x - slot.container.x) * k;
        slot.container.y += (slot.target.y - slot.container.y) * k;
        slot.container.scale.set(invScale);
        const want = slot.alive ? 1 : 0;
        const step = dt / CURSOR_FADE_MS;
        const cur = slot.container.alpha;
        slot.container.alpha = cur < want ? Math.min(want, cur + step) : Math.max(want, cur - step);
        if (!slot.alive && slot.container.alpha <= 0.001) {
          cursorsContainer.removeChild(slot.container);
          slot.container.destroy({ children: true });
          cursorSlots.delete(by);
        }
      }
    }

    if (pingBursts.length > 0) {
      const now = performance.now();
      for (let i = pingBursts.length - 1; i >= 0; i--) {
        const b = pingBursts[i]!;
        const t = Math.min(1, (now - b.startedAt) / PING_BURST_MS);
        const eased = 1 - (1 - t) * (1 - t); // ease-out quad
        b.container.scale.set(0.3 + eased * 1.5);
        b.container.alpha = 1 - eased;
        if (t >= 1) {
          vfxContainer.removeChild(b.container);
          b.container.destroy({ children: true });
          pingBursts.splice(i, 1);
        }
      }
    }

    // The chain link's focus ring, redrawn around each focused token's live container.
    if (focusIds.length > 0) {
      focusRing.clear();
      for (const id of focusIds) {
        const entry = tokenEntries.get(id);
        if (entry === undefined) continue;
        const { container } = entry;
        const cx = container.x + container.width / 2;
        const cy = container.y + container.height / 2;
        const r = Math.max(container.width, container.height) * 0.62;
        focusRing.circle(cx, cy, r).stroke({ width: 3, color: FOCUS_RING_COLOR, alpha: 0.95 });
      }
    }

    // Floating combat text: rise + fade over FLOAT_RISE_MS, then self-remove (the Pixi twin of the
    // deleted `<DamageFloat>`'s once-on-mount tween).
    if (floatEntries.length > 0) {
      const now = performance.now();
      for (let i = floatEntries.length - 1; i >= 0; i--) {
        const f = floatEntries[i]!;
        if (f.caption) {
          // A caption holds still, then fades: it is read, not watched rise.
          const age = now - f.startedAt;
          f.node.alpha = age <= CAPTION_HOLD_MS ? 1 : Math.max(0, 1 - (age - CAPTION_HOLD_MS) / CAPTION_FADE_MS);
          if (age >= CAPTION_HOLD_MS + CAPTION_FADE_MS) {
            vfxContainer.removeChild(f.node);
            f.node.destroy({ children: true });
            floatEntries.splice(i, 1);
          }
          continue;
        }
        const t = Math.min(1, (now - f.startedAt) / FLOAT_RISE_MS);
        const eased = 1 - (1 - t) * (1 - t); // ease-out quad
        f.node.y = f.startY - eased * FLOAT_RISE_DIST;
        f.node.alpha = 1 - eased;
        if (t >= 1) {
          vfxContainer.removeChild(f.node);
          f.node.destroy();
          floatEntries.splice(i, 1);
        }
      }
    }

    // The attack looks: their own scene clock (a crit's hit-stop freezes it), advanced by the frame.
    if (attackFxScene.busy) attackFxScene.step(ticker.deltaMS);

    // Impact bursts: scale up + fade out over IMPACT_BURST_MS, then self-remove (the Pixi twin of the
    // deleted `<ImpactBurst>`).
    if (impactEntries.length > 0) {
      const now = performance.now();
      for (let i = impactEntries.length - 1; i >= 0; i--) {
        const b = impactEntries[i]!;
        const t = Math.min(1, (now - b.startedAt) / IMPACT_BURST_MS);
        const eased = 1 - (1 - t) * (1 - t); // ease-out quad
        b.container.scale.set(0.35 + eased * 1.75);
        b.container.alpha = 1 - eased;
        if (t >= 1) {
          vfxContainer.removeChild(b.container);
          b.container.destroy({ children: true });
          impactEntries.splice(i, 1);
        }
      }
    }

    // Area-effect clouds: a slow, cheap "breathe" — alpha only, on already-built fill geometry (the
    // SAME ticker-driven transform/alpha pattern as the halo pulse above), so a live cloud reads as
    // something drifting in the air rather than a static painted tile. A per-index phase offset keeps
    // multiple simultaneous clouds from pulsing in lockstep.
    if (areaEffectFills.length > 0) {
      const t = performance.now() / 1000;
      for (let i = 0; i < areaEffectFills.length; i++) {
        const phase = (Math.sin(t * ((2 * Math.PI) / AREA_EFFECT_BREATH_PERIOD_S) + i * 0.9) + 1) / 2;
        areaEffectFills[i]!.alpha = AREA_EFFECT_BREATH_MIN + phase * AREA_EFFECT_BREATH_RANGE;
      }
    }
  });

  // ── Resolution: the canvas at device resolution, and board text re-rasterized at the settled zoom.
  // `setView` feeds `viewSettled`; once the view holds still, every board `Text` gets
  // `dpr × textLevel(scale)` (`boardResolution.ts`). Layers that build text call `sharpenText` so a
  // new label starts at the current level instead of the renderer default. The cursor chips are
  // counter-scaled against the zoom, so they stay on auto resolution (the renderer's).
  const viewSettled = createViewSettled();
  let settledScale = 1;
  let textRasterMs = 0;
  const currentView = (): SettledView => ({ x: world.x, y: world.y, scale: world.scale.x, dpr: app.renderer.resolution });
  const sharpenText = (root: Container): void => {
    applyTextResolution(root, textResolution(settledScale, app.renderer.resolution), cursorsContainer);
  };
  // Pixi rasterizes a changed text lazily, inside the next render, so the hitch is timed across
  // that one render: stamp just before the render ticker callback (`UPDATE_PRIORITY.LOW`) and read
  // just after it.
  const timeNextRender = (): void => {
    let startedAt = 0;
    app.ticker.addOnce(() => (startedAt = performance.now()), undefined, UPDATE_PRIORITY.LOW + 1);
    app.ticker.addOnce(() => (textRasterMs = performance.now() - startedAt), undefined, UPDATE_PRIORITY.LOW - 1);
  };
  const terrainLayer = createTerrainLayer({ container: terrainContainer, ticker: app.ticker });
  let boardSize: { width: number; height: number } | null = null;
  let terrainState: { terrain: TerrainMap; edges: EdgeMap; ground: EncounterGround } | null = null;
  const repaintTerrain = (): void => {
    if (boardSize && terrainState) terrainLayer.setTerrain(boardSize, terrainState);
    drawTerrainMechanics();
  };

  // The terrain mechanics overlay. Drawn when it turns on and when the terrain map or the board
  // changes; a zoom settle only re-lays the labels (constant screen size, compact when cramped).
  // With the overlay off, the Inspect tool's focus cell draws just the pieces containing it.
  let terrainMechanicsOn = false;
  let terrainMechanicsFocus: string | null = null;
  let terrainMechanicsAll: { terrain: TerrainMap; board: { width: number; height: number }; pieces: TerrainMechanicsPiece[] } | null = null;
  let terrainMechanicsLabels: { piece: TerrainMechanicsPiece; text: Text }[] = [];
  function layTerrainMechanicsLabels(): void {
    for (const { piece, text } of terrainMechanicsLabels) {
      text.text = mechanicsLabelText(piece, settledScale);
      text.scale.set(1 / settledScale);
    }
  }
  function clearTerrainMechanics(): void {
    clearContainer(terrainMechanicsContainer);
    clearContainer(terrainMechanicsLabelsContainer);
    terrainMechanicsLabels = [];
  }
  function drawTerrainMechanics(): void {
    const visible = terrainMechanicsOn || terrainMechanicsFocus !== null;
    terrainMechanicsContainer.visible = visible;
    terrainMechanicsLabelsContainer.visible = visible;
    if (!visible) {
      clearTerrainMechanics();
      terrainMechanicsAll = null;
      return;
    }
    if (!boardSize || !terrainState) return;
    // Pieces are computed once per terrain map and board; a focus change only filters them.
    if (terrainMechanicsAll?.terrain !== terrainState.terrain || terrainMechanicsAll.board !== boardSize) {
      terrainMechanicsAll = { terrain: terrainState.terrain, board: boardSize, pieces: terrainMechanicsPieces(terrainState.terrain, boardSize) };
    }
    const all = terrainMechanicsAll.pieces;
    const pieces = terrainMechanicsOn || terrainMechanicsFocus === null ? all : piecesContaining(all, terrainMechanicsFocus);
    const drawn = terrainMechanicsLabels.map((l) => l.piece);
    if (drawn.length === pieces.length && drawn.every((p, i) => p === pieces[i])) return;
    clearTerrainMechanics();
    const style = new TextStyle({ fill: MECHANICS_LABEL_INK, fontFamily: RULER_FONT_FAMILY, fontWeight: "bold", fontSize: MECHANICS_LABEL_FONT, stroke: LABEL_UNDER });
    const { width: cols, height: rows } = boardSize;
    terrainMechanicsLabels = pieces.map((piece) => {
      drawBand(terrainMechanicsContainer, piece.cells, { cols, rows, style: MECHANICS_BAND });
      const { x, y } = parseTileKey(piece.labelCell);
      const text = new Text({ text: piece.label, style, anchor: 0.5 });
      text.position.set((x + 0.5) * CELL, (y + 0.5) * CELL);
      terrainMechanicsLabelsContainer.addChild(text);
      return { piece, text };
    });
    layTerrainMechanicsLabels();
    sharpenText(terrainMechanicsLabelsContainer);
  }
  viewSettled.subscribe((view) => terrainLayer.settle(screenWorldRect(view, app.screen), chunkDensity(view.scale, view.dpr)));
  viewSettled.subscribe((view) => {
    settledScale = view.scale;
    layTerrainMechanicsLabels();
    if (applyTextResolution(world, textResolution(view.scale, view.dpr), cursorsContainer).changed > 0) timeNextRender();
  });

  // Follow the device pixel ratio (a window dragged to another monitor, or browser zoom). A
  // `resolution` media query matches only the ratio it was built for, so each change re-arms it.
  let dprQuery: MediaQueryList | null = null;
  const onDprChange = (): void => {
    armDprQuery();
    const resolution = boardResolution(window.devicePixelRatio);
    if (resolution === app.renderer.resolution) return;
    app.renderer.resolution = resolution;
    viewSettled.flush(currentView());
  };
  const armDprQuery = (): void => {
    dprQuery?.removeEventListener("change", onDprChange);
    dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    dprQuery.addEventListener("change", onDprChange);
  };
  armDprQuery();

  // The authoring ghosts on screen, so a tile swatch that lands later redraws only the current one.
  let authoringPreview: AuthoringPreviewSpec[] = [];
  function drawAuthoringPreview(specs: AuthoringPreviewSpec[]): void {
    clearContainer(authoringPreviewContainer);
    authoringPreview = specs;
    // A tile whose swatch hasn't painted draws the flat tint now and redraws once it lands, if this
    // preview is still the one on screen.
    for (const spec of specs) {
      if (spec.kind === "line" || !spec.swatch) continue;
      const swatch: SwatchSpec = { kind: "tile", cell: spec.swatch, shape: "fill" };
      if (tileSwatches.peek(swatch)) continue;
      tileSwatches.get(swatch).then(
        () => {
          if (authoringPreview === specs) drawAuthoringPreview(specs);
        },
        () => undefined,
      );
    }
    for (const spec of specs) {
      if (spec.kind === "cells") drawCellsPreview(authoringPreviewContainer, spec);
      else if (spec.kind === "box") drawBoxPreview(authoringPreviewContainer, spec);
      else drawLinePreview(authoringPreviewContainer, spec);
    }
    sharpenText(authoringPreviewContainer);
  }

  return {
    app,
    world,
    resize() {
      // `app.resize()` is ResizePlugin's synchronous re-measure of `resizeTo` (the host div) — not
      // `queueResize`, which defers a frame and would show one frame of the stale canvas.
      app.resize();
    },
    setView(view) {
      world.position.set(view.x, view.y);
      world.scale.set(view.scale);
      viewSettled.note(currentView());
    },
    onViewSettled(listener) {
      return viewSettled.subscribe(listener);
    },
    debugRenderResolution() {
      return {
        resolution: app.renderer.resolution,
        dpr: window.devicePixelRatio,
        backing: [app.canvas.width, app.canvas.height],
        css: [app.canvas.clientWidth, app.canvas.clientHeight],
      };
    },
    debugTextRaster() {
      const resolutions: Record<string, number> = {};
      const walk = (node: Container): void => {
        if (node === cursorsContainer) return;
        if (node instanceof Text) resolutions[String(node.resolution)] = (resolutions[String(node.resolution)] ?? 0) + 1;
        for (const child of node.children) walk(child);
      };
      walk(world);
      return { scale: settledScale, level: textLevel(settledScale), lastMs: textRasterMs, resolutions };
    },
    setBoard(cols, rows) {
      clearContainer(boardContainer);
      boardContainer.addChild(buildBoardContainer(cols, rows));
      sharpenText(boardContainer);
      clearContainer(fullGridContainer);
      fullGridContainer.addChild(buildFullGrid(cols, rows));
      boardSize = { width: Math.max(1, cols), height: Math.max(1, rows) };
      repaintTerrain();
    },
    setTerrain(terrain, edges, ground) {
      terrainState = { terrain, edges, ground };
      repaintTerrain();
    },
    setFullGrid(visible) {
      fullGridContainer.visible = visible;
    },
    debugTerrainBuild() {
      return terrainLayer.stats();
    },
    debugFullGrid() {
      return fullGridContainer.visible;
    },
    setTerrainMechanics(on) {
      terrainMechanicsOn = on;
      drawTerrainMechanics();
    },
    setTerrainMechanicsFocus(cellKey) {
      terrainMechanicsFocus = cellKey;
      drawTerrainMechanics();
    },
    debugTerrainMechanics() {
      return {
        on: terrainMechanicsOn,
        focus: terrainMechanicsFocus,
        pieces: terrainMechanicsLabels.map(({ piece, text }) => ({ label: piece.label, compact: piece.compact, shown: text.text, cells: piece.cells, labelCell: piece.labelCell })),
      };
    },
    setRooms(rooms) {
      clearContainer(roomsContainer);
      for (const room of Object.values(rooms)) {
        const rc = buildRoomContainer(room);
        if (rc) roomsContainer.addChild(rc);
      }
      sharpenText(roomsContainer);
    },
    setSitrepZones(zones) {
      clearContainer(sitrepZonesContainer);
      for (const spec of zones) {
        const zc = buildSitrepZoneContainer(spec);
        if (zc) sitrepZonesContainer.addChild(zc);
      }
      sharpenText(sitrepZonesContainer);
    },
    setAreaEffects(areaEffects) {
      clearContainer(areaEffectsContainer);
      areaEffectFills = [];
      for (const area of Object.values(areaEffects)) {
        const cells = areaEffectCells(area);
        const fill = buildAreaEffectFill(cells, areaEffectStyle(area.grants));
        areaEffectsContainer.addChild(fill);
        areaEffectFills.push(fill);
        const board = { cols: Math.max(...cells.map((c) => c.x)) + 1, rows: Math.max(...cells.map((c) => c.y)) + 1 };
        const edge = area.grants === "outside_attack_difficulty" ? SHIELD_DOME_EDGE : AREA_EFFECT_EDGE;
        drawBand(areaEffectsContainer, cells.map((c) => `${c.x},${c.y}`), { ...board, style: edge });
      }
    },
    setDroneAuras(rings) {
      clearContainer(droneAurasContainer);
      droneAuraCount = rings.length;
      for (const ring of rings) {
        const cells = ring.cells.filter((c) => c.x >= 0 && c.y >= 0);
        if (cells.length === 0) continue;
        const board = { cols: Math.max(...cells.map((c) => c.x)) + 1, rows: Math.max(...cells.map((c) => c.y)) + 1 };
        drawBand(droneAurasContainer, cells.map((c) => `${c.x},${c.y}`), { ...board, style: DRONE_AURA_EDGE });
      }
    },
    setAuthoringPreview(specs) {
      drawAuthoringPreview(specs);
    },
    setTokens(combatants, opts) {
      // Incremental reconcile — see `TokenEntry`'s doc for the full "why" (a destroy-everything
      // version races `pixiAnimator.ts`'s in-flight tweens/drags and snaps a forced/remote move's
      // slide). Three cases per combatant, by id:
      //   - CREATE (id not in `tokenEntries`): build a fresh container + label at its resting
      //     anchor, then register with the animator.
      //   - RESTYLE (id persists): keep the SAME container/label — never destroyed, never
      //     re-registered — reposition ONLY if the resting anchor actually moved (`restMinX/Y`,
      //     compared against, not the live `.x`/`.y` a tween/drag may be writing), and rebuild the
      //     child graphics ONLY if `tokenStyleKey` actually changed.
      //   - REMOVE (a previously-built id absent from this call): unregister + destroy, below.
      // ONE selection fact, and this is the one that actually flows. `opts.selectedId` is the live
      // selection every caller already passes (`useBoardSync` mirrors `CombatApp`'s state into it on
      // every change), and it is what the per-token `selected` styling below has always read;
      // `selectedTokenId` was a second copy of the same fact that only `setSelection` wrote — and
      // NOTHING in the repo has ever called `setSelection`, so it sat permanently `null`. Assigning
      // it here collapses the two rather than adding a third for the raised label layer to read.
      //
      // **Side effect, deliberate and flagged:** `rebuildHalo` reads this same field, so the
      // breathing selection-halo ring (documented as shipped in `CAPABILITIES.md` §6 and
      // `ARCHITECTURE.md`, dead in practice since the Pixi port) starts rendering again. Deleting
      // this one line turns it back off without touching the label reveal's own correctness — the
      // reveal would simply lose its selection half and keep its hover half. It is on the
      // developer's Phase 3.3 eyeball list (`projects/crowded-label-placement.md`).
      selectedTokenId = opts.selectedId;
      lastImageCtx = opts.imageCtx;

      assetLayer.set(placedAssets(combatants));
      const seen = new Set<string>();
      for (const c of combatants) {
        if (c.pos == null) continue;
        seen.add(c.id);
        const untargetable = opts.untargetableIds?.has(c.id) ?? false;
        const selected = c.id === opts.selectedId;
        const mine = controlsCombatant(c, opts.viewerSlot);
        const image = tokenImageFor(c, opts.imageCtx);
        const chrome = opts.chrome[c.id];
        const sq = asSquareCell(c.pos);
        const { minX, minY } = footprintBox(sq, c.base.size);
        const existing = tokenEntries.get(c.id);

        if (!existing) {
          const container = new Container();
          container.x = minX * CELL;
          container.y = minY * CELL;
          const { side, accent, art } = renderTokenLook(container, c, { untargetable, selected, mine, image, chrome });
          tokensContainer.addChild(container);
          tokenEntries.set(c.id, { container, side, accent, restMinX: minX, restMinY: minY, styleKey: tokenStyleKey(c, { untargetable, selected, mine, image, chrome }), mine, art, chrome });
          // Register the freshly-built container with the animator — the Pixi analogue of "a token
          // registers its node on mount".
          nodeRegistry.registerNode(c.id, container);

          // Label — one Container per token (pill background + single-line truncated text + leader
          // line), per `LabelEntry`'s doc. Built at (0,0) here; the solve pass at the tail of this
          // function (below) positions every label (new and persisting alike) in one place, so a
          // freshly-created label's real position is never duplicated/first-guessed here.
          const labelContainer = new Container();
          const bg = new Graphics();
          const leader = new Graphics();
          const tick = new Graphics();
          const text = new Text({ text: c.name, style: new TextStyle({ fill: TOKEN_INK, fontFamily: "JetBrains Mono, monospace", fontSize: TOKEN_LABEL_FONT }) });
          labelContainer.addChild(bg, leader, tick, text);
          labelsContainer.addChild(labelContainer);
          const labelEntry: LabelEntry = { container: labelContainer, bg, text, leader, tick, w: 0, h: 0, dx: 0, dy: 0, where: "attached", slot: 0, onReveal: isDrawnAsAsset(c) };
          layoutLabelPill(labelEntry, tokenLabelText(c));
          labelEntries.set(c.id, labelEntry);
          continue;
        }

        // RESTYLE — reposition only on a genuine resting-anchor change (a commit or a hard snap),
        // never against the container's live position (see `TokenEntry`'s doc).
        if (minX !== existing.restMinX || minY !== existing.restMinY) {
          existing.container.x = minX * CELL;
          existing.container.y = minY * CELL;
          existing.restMinX = minX;
          existing.restMinY = minY;
        }
        // Re-affirm this token's place at the END of the display list / label list, restoring
        // combatants-array z-order every call (`Container.addChild` on an existing child moves it,
        // it doesn't duplicate) — cheap, and avoids a full-container rebuild just to keep ordering.
        tokensContainer.addChild(existing.container);
        // Restore combatants-array z-order among the ORDINARY labels. A revealed label is pulled
        // back out into `labelRevealContainer` by `syncLabelReveal` at the tail of this function.
        const labelEntry = labelEntries.get(c.id);
        if (labelEntry) labelsContainer.addChild(labelEntry.container);

        const styleKey = tokenStyleKey(c, { untargetable, selected, mine, image, chrome });
        if (styleKey !== existing.styleKey) {
          const { side, accent, art } = renderTokenLook(existing.container, c, { untargetable, selected, mine, image, chrome });
          existing.side = side;
          existing.accent = accent;
          existing.art = art;
          existing.styleKey = styleKey;
          existing.mine = mine;
          existing.chrome = chrome;
          // `styleKey` folds in `c.name` (and footprint size, which changes the token rect the solver
          // feeds off of) — update the pill in place; never rebuild the container (see `TokenEntry`'s
          // doc on why RESTYLE never touches `pixiAnimator.ts`-owned containers wholesale).
          if (labelEntry) layoutLabelPill(labelEntry, tokenLabelText(c));
        }
      }
      // Adjacent hexes can touch now that they overflow their footprints (`tokenOverflow`), so the
      // ACTIVE unit, then the selection, draws over its neighbours.
      const activeEntry = [...tokenEntries.values()].find((e) => e.chrome?.state === "active");
      if (activeEntry) tokensContainer.addChild(activeEntry.container);
      const selectedEntry = opts.selectedId ? tokenEntries.get(opts.selectedId) : undefined;
      if (selectedEntry) tokensContainer.addChild(selectedEntry.container);

      // REMOVE — every previously-built id no longer placed this call (dropped from the encounter, or
      // un-placed). Unregister BEFORE destroying (same "clear on unmount" contract the old
      // always-rebuild version kept for every id, now scoped to just the ones that actually left) —
      // nothing here is mid-flight for a genuinely removed token, so the `b1a1636` destroyed-node
      // guard in `pixiAnimator.ts` is the correct (and sufficient) safety net for any tween that WAS
      // still running against it.
      for (const id of [...tokenEntries.keys()]) {
        if (seen.has(id)) continue;
        nodeRegistry.unregisterNode(id);
        const entry = tokenEntries.get(id)!;
        tokensContainer.removeChild(entry.container);
        entry.container.destroy({ children: true });
        tokenEntries.delete(id);
        const labelEntry = labelEntries.get(id);
        if (labelEntry) {
          // `.parent`, not `labelsContainer` — a hovered/selected token's label is parented to
          // `labelRevealContainer` (see `syncLabelReveal`), and removing it from the wrong container
          // is a silent no-op that would leave the destroyed container mounted.
          labelEntry.container.parent?.removeChild(labelEntry.container);
          labelEntry.container.destroy({ children: true });
          labelEntries.delete(id);
        }
      }

      // Solve label placement once per call, AFTER the CREATE/
      // RESTYLE/REMOVE reconcile above so every currently-live label is fed exactly the currently-
      // live token set. Token rects are the RESTING position (`restMinX/Y`, not the container's live
      // `.x`/`.y`, which a drag/tween may be actively writing — see `TokenEntry`'s doc) so an
      // in-flight drag never perturbs the solve; the ticker-follow above re-applies the solved offset
      // against the LIVE position every frame, which is what actually tracks a drag. `prevSlot` is
      // fed from each label's OWN last-solved candidate index, which is what stops a settled label
      // moving when some unrelated token does. A brand-new label defaults to 0, and a first-ever
      // solve is still unaffected by that: candidate 0 is the cheapest candidate anyway when it is
      // free, and when it is blocked it is excluded outright (collision is a hard filter, not a
      // cost), leaving every remaining candidate carrying the same change penalty — so their
      // relative order, and therefore the choice, is exactly what it would have been with no
      // remembered placement at all.
      // `footprintCells` is what lets a size-2+ unit label INSIDE its own footprint instead of
      // eating its small neighbours' slots. `obstacles` are the status rows drawn above a size-1
      // footprint (`pixiTokenChrome.ts`); every other chrome mark stays inside the token rect.
      const items: LabelItem[] = [];
      const obstacles: Rect[] = [];
      for (const [id, labelEntry] of labelEntries) {
        const tokenEntry = tokenEntries.get(id);
        if (!tokenEntry) continue; // a label only ever exists alongside its token — defensive only.
        if (labelEntry.onReveal) continue; // a map asset's label is placed below, outside the solve.
        // The token rect is the DRAWN extent: the hex overflows its footprint (`tokenOverflow`), and a
        // name must not sit on that overflow. Offsets come back against this rect and are shifted
        // back to the footprint's top-left below.
        const over = tokenOverflow(tokenEntry.side, CELL);
        items.push({
          id,
          token: { x: tokenEntry.restMinX * CELL - over.x, y: tokenEntry.restMinY * CELL - over.y, w: tokenEntry.side + 2 * over.x, h: tokenEntry.side + 2 * over.y },
          label: { w: labelEntry.w, h: labelEntry.h },
          // `side` is `footprintBox().n * CELL` (see `renderTokenLook`), so this divides back out to
          // the exact integer cell count the solver's inside-placement threshold reads.
          footprintCells: tokenEntry.side / CELL,
          // An inside label sits clear of the hex token's HP trim.
          insideBottom: Math.floor(insideLabelFloor(tokenHex(tokenEntry.side, CELL), labelEntry.w) + over.y),
          prevSlot: labelEntry.slot,
        });
        const row = tokenEntry.chrome ? tokenChromeObstacle(tokenEntry.chrome, { side: tokenEntry.side, cell: CELL }) : null;
        if (row) obstacles.push({ ...row, x: row.x + tokenEntry.restMinX * CELL, y: row.y + tokenEntry.restMinY * CELL });
      }
      const placements = solveLabelLayout(items, obstacles);
      for (const [id, labelEntry] of labelEntries) {
        const tokenEntry = tokenEntries.get(id);
        if (tokenEntry && labelEntry.onReveal) {
          placeRevealOnlyLabel(labelEntry, tokenEntry.side);
          continue;
        }
        const solved = placements.get(id);
        if (!tokenEntry || !solved) continue;
        const over = tokenOverflow(tokenEntry.side, CELL);
        const placed = { ...solved, dx: solved.dx - over.x, dy: solved.dy - over.y };
        labelEntry.dx = placed.dx;
        labelEntry.dy = placed.dy;
        labelEntry.where = placed.where;
        labelEntry.slot = placed.slot;
        labelEntry.container.x = tokenEntry.restMinX * CELL + placed.dx;
        labelEntry.container.y = tokenEntry.restMinY * CELL + placed.dy;

        // Leader line — drawn iff `where === "displaced"` (see `LabelEntry`'s doc), built HERE in
        // container-local space at solve time, never per frame. Connects the pill-boundary point
        // nearest the token to the token-boundary point nearest the pill, computed in the label
        // container's own local frame (token top-left is `(-dx,-dy)` there, since
        // `container.x/y = token.x/y + dx/dy`). An `"inside"` placement draws no line — the pill is
        // already within the footprint it names, and `nearestBoundaryPoint` below assumes the two
        // rects are disjoint, which an inside placement is not.
        labelEntry.leader.clear();
        if (placed.where === "displaced") {
          const labelRect = { x: 0, y: 0, w: labelEntry.w, h: labelEntry.h };
          const tokenRect = { x: -placed.dx, y: -placed.dy, w: tokenEntry.side, h: tokenEntry.side };
          const labelCenter = { x: labelEntry.w / 2, y: labelEntry.h / 2 };
          const tokenCenter = { x: tokenRect.x + tokenEntry.side / 2, y: tokenRect.y + tokenEntry.side / 2 };
          const p1 = nearestBoundaryPoint(labelRect, tokenCenter);
          const p2 = nearestBoundaryPoint(tokenRect, labelCenter);
          labelEntry.leader.moveTo(p1.x, p1.y).lineTo(p2.x, p2.y).stroke({ width: 1, color: TOKEN_INK, alpha: TOKEN_LABEL_LEADER_ALPHA });
        }

        // Hidden label — the solver found nowhere clean to put this name and declined to draw it on
        // top of something. A `HIDDEN_LABEL_TICK_PX` square goes at local `(w/2, 0)`, which IS the
        // token's bottom centre (`dx` is candidate 0's offset, so the pill's horizontal centre is the
        // token's and local y=0 is `GAP` below the token's bottom edge), so the player can see a name
        // exists. The container itself stays visible and stays in the ticker's follow set, so the
        // tick rides a drag exactly as a drawn pill does. Whether the tick or the PILL is the thing
        // actually on screen is `syncLabelReveal`'s call, made once at the tail of this function —
        // hovering or selecting the token shows the pill here instead.
        labelEntry.tick.clear();
        if (placed.where === "hidden") {
          labelEntry.tick
            .rect(labelEntry.w / 2 - HIDDEN_LABEL_TICK_PX / 2, 0, HIDDEN_LABEL_TICK_PX, HIDDEN_LABEL_TICK_PX)
            .fill({ color: TOKEN_INK, alpha: TOKEN_LABEL_LEADER_ALPHA });
        }
      }

      // Layer + visibility for the current hover/selection, over the freshly-solved `where` values.
      syncLabelReveal();
      // The badges go around the labels just solved.
      solveBadges();

      // The just-reconciled set may have changed the selected token's accent/side (or dropped it) —
      // resync the halo geometry (still only rebuilt here, never per frame).
      rebuildHalo();
      // The chrome just applied may have moved the ACTIVE state; the beacon follows it.
      rebuildBeacon();
      sharpenText(tokensContainer);
      sharpenText(labelsContainer);
      sharpenText(labelRevealContainer);
    },
    moveToken(id, topLeft) {
      const entry = tokenEntries.get(id);
      if (entry) {
        entry.container.x = topLeft.x;
        entry.container.y = topLeft.y;
      }
    },
    setSelection(id) {
      // Kept as the handle's imperative seam, but note that nothing calls it today: the live
      // selection reaches this module through `setTokens`' `opts.selectedId`, which writes the very
      // same field (see the assignment at the top of `setTokens`).
      selectedTokenId = id;
      syncLabelReveal();
      rebuildHalo();
    },
    setHoveredToken(id) {
      if (id === hoveredTokenId) return;
      hoveredTokenId = id;
      syncLabelReveal();
    },
    setMoveRange(cells, cols, rows) {
      clearContainer(moveRangeContainer);
      moveRangeCells = cells;
      drawBand(moveRangeContainer, cells, { cols, rows, style: MOVE_RANGE_BAND });
      drawBoostBand();
    },
    setBoostRange(cells, cols, rows) {
      boostRange = { cells, cols, rows };
      drawBoostBand();
    },
    debugBoostRangeCount() {
      return boostRangeContainer.children.length;
    },
    setSensorRange(cells, cols, rows) {
      clearContainer(sensorRangeContainer);
      drawBand(sensorRangeContainer, cells, { cols, rows, style: SENSOR_RANGE_BAND });
    },
    setThreatRings({ rings, cover }, cols, rows) {
      clearContainer(threatRingContainer);
      threatRingCount = 0;
      threatRingKeys = [];
      threatRingHatchCount = 0;
      // The hatch first, so every ring's dashed edge draws on top of it (as in `setTargeting`).
      for (const [key, grade] of cover) {
        const { x, y } = parseTileKey(key);
        if (x < 0 || x >= cols || y < 0 || y >= rows) continue;
        const hatch = buildHatchCell(ARMED_COVER_HATCH[grade]);
        hatch.x = x * CELL;
        hatch.y = y * CELL;
        threatRingContainer.addChild(hatch);
        threatRingHatchCount++;
      }
      for (const keys of rings) {
        const before = threatRingContainer.children.length;
        drawBand(threatRingContainer, keys, { cols, rows, style: ATTACK_RANGE_BAND });
        if (threatRingContainer.children.length > before) {
          threatRingCount++;
          threatRingKeys.push([...keys].sort());
        }
      }
    },
    debugThreatRingCount() {
      return threatRingCount;
    },
    debugThreatRingKeys() {
      return threatRingKeys;
    },
    debugThreatRingHatchCount() {
      return threatRingHatchCount;
    },
    setMovePath({ cells, boostCells }, cols, rows) {
      clearContainer(movePathContainer);
      drawBand(movePathContainer, cells, { cols, rows, style: movePathRefused ? MOVE_PATH_REFUSED_BAND : MOVE_PATH_BAND });
      drawBand(movePathContainer, boostCells, { cols, rows, style: MOVE_PATH_BOOST_BAND });
      movePathBoostCells = boostCells.size;
    },
    setMovePathRefused(refused) {
      movePathRefused = refused;
    },
    debugMovePathRefused() {
      return movePathRefused && movePathContainer.children.length > 0;
    },
    setRemoteMovePaths(previews, cols, rows) {
      clearContainer(remoteMovePathContainer);
      for (const p of previews) {
        const hue = slotHue(p.by);
        const style: BandStyle = { wash: `hsla(${hue}, 70%, 60%, 0.26)`, edge: `hsl(${hue}, 85%, 80%)`, width: 2 };
        drawBand(remoteMovePathContainer, p.cells, { cols, rows, style });
      }
    },
    createGhost(c, topLeft) {
      const existing = ghostEntries.get(c.id);
      if (existing) {
        dragGhostContainer.removeChild(existing);
        existing.destroy({ children: true });
      }
      const container = new Container();
      container.x = topLeft.x;
      container.y = topLeft.y;
      // The same art the real token shows; before the first `setTokens` there is no ctx, and the
      // masked ring is the one answer that cannot leak anything.
      const image = lastImageCtx ? tokenImageFor(c, lastImageCtx) : MASKED_IMAGE;
      renderTokenLook(container, c, { untargetable: false, selected: false, mine: false, image, chrome: undefined, ghost: true });
      container.alpha = DRAG_GHOST_ALPHA; // a flat fade over whatever look it just drew — see the constant's doc
      dragGhostContainer.addChild(container);
      sharpenText(container);
      ghostEntries.set(c.id, container);
    },
    moveGhost(id, topLeft) {
      const entry = ghostEntries.get(id);
      if (entry) {
        entry.x = topLeft.x;
        entry.y = topLeft.y;
      }
    },
    snapGhost(id, topLeft) {
      const entry = ghostEntries.get(id);
      if (entry) {
        entry.x = topLeft.x;
        entry.y = topLeft.y;
      }
    },
    clearGhost(id) {
      const entry = ghostEntries.get(id);
      if (entry) {
        dragGhostContainer.removeChild(entry);
        entry.destroy({ children: true });
        ghostEntries.delete(id);
      }
    },
    setPendingDeploy(cells, opts) {
      clearContainer(pendingDeployContainer);
      if (!cells || cells.length === 0) return;
      const { size, cols, rows } = opts;
      for (const key of cells) {
        const { x, y } = parseTileKey(key);
        if (x < 0 || x >= cols || y < 0 || y >= rows) continue;
        // The SAME footprint geometry + token a real deployed section draws with (the neutral generic
        // ring {@link SCENERY_TOKEN_LOOK} gives any non-character), so the pending mark reads as "the
        // section goes HERE" rather than as some unrelated marker — only faded, so it can never be
        // mistaken for a token that already exists.
        const { minX, minY, n } = footprintBox({ grid: "square", x, y }, size);
        const g = new Container();
        drawTokenSprite(g, { side: n * CELL, image: GENERIC_IMAGE, accent: SCENERY_TOKEN_LOOK.accent, fill: SCENERY_TOKEN_LOOK.fill, selected: false, mine: false, destroyed: false, hidden: false, concealed: false, openDoor: false });
        g.x = minX * CELL;
        g.y = minY * CELL;
        pendingDeployContainer.addChild(g);
      }
      pendingDeployContainer.alpha = PENDING_DEPLOY_ALPHA;
    },
    setTargeting(cellSightlines, opts) {
      const { attackAffected, attackLosExcluded, cols, rows } = opts;
      clearContainer(attackOverlayContainer);
      if (cellSightlines) {
        // Cover renders as a per-cell diagonal-hatch UNDERLAY, added before the range band so the
        // dashed boundary always draws on top of (never fights) the hatch.
        const inRange: string[] = [];
        for (const sl of cellSightlines) {
          if (!sl.visible) continue;
          const { x, y } = parseTileKey(sl.key);
          if (x < 0 || x >= cols || y < 0 || y >= rows) continue;
          if (sl.cover !== "none") {
            const hatch = buildHatchCell(sl.cover === "hard" ? ARMED_COVER_HATCH.hard : ARMED_COVER_HATCH.soft);
            hatch.x = x * CELL;
            hatch.y = y * CELL;
            attackOverlayContainer.addChild(hatch);
          }
          inRange.push(sl.key);
        }
        drawBand(attackOverlayContainer, inRange, { cols, rows, style: ATTACK_RANGE_BAND });
      }
      if (attackAffected) drawBand(attackOverlayContainer, attackAffected, { cols, rows, style: ATTACK_AFFECTED_BAND });
      // LoS-excluded marker — drawn LAST so it sits on top of the affected-cells band above: a
      // sheltered token's cell stays inside the template (RAW never shrinks the painted area) but
      // reads veiled, a dark cell with a light rim.
      if (attackLosExcluded) {
        for (const key of attackLosExcluded) {
          const { x, y } = parseTileKey(key);
          if (x < 0 || x >= cols || y < 0 || y >= rows) continue;
          const veil = new Graphics().rect(1, 1, CELL - 2, CELL - 2).fill(LOS_VEIL.fill).stroke({ width: LOS_VEIL.width, color: LOS_VEIL.border });
          veil.x = x * CELL;
          veil.y = y * CELL;
          attackOverlayContainer.addChild(veil);
        }
      }
    },
    setAnnotations(annotations, selectedId) {
      drawnAnnotationIds = Object.keys(annotations);
      clearContainer(annotationsContainer);
      annotationsContainer.addChild(buildAnnotationsContainer(annotations, selectedId));
    },
    setDrawPreview(spec) {
      clearContainer(drawPreviewContainer);
      if (spec) drawPreviewContainer.addChild(buildDrawPreviewContainer(spec));
    },
    setMeasures(specs, board) {
      clearContainer(measureContainer);
      for (const spec of specs) measureContainer.addChild(buildMeasureContainer(spec, board));
      sharpenText(measureContainer);
    },
    setCursors(cursors) {
      const incoming = new Map(cursors.map((c) => [c.by, c]));
      for (const [by, c] of incoming) {
        const slot = cursorSlots.get(by);
        if (!slot) {
          const { container, chip, text } = buildCursorContainer(by, c.label);
          container.x = c.at.x;
          container.y = c.at.y;
          cursorsContainer.addChild(container);
          cursorSlots.set(by, { container, target: c.at, alive: true, label: c.label, chip, text });
        } else {
          slot.target = c.at;
          slot.alive = true;
          // The late-resolving-label race: a cursor event can arrive before the slot-presence
          // broadcast that names it, so `buildCursorContainer` above may have run with the
          // fallback label already baked into the chip. Rebuild just the chip (not the whole
          // reticle, which is mid-lerp) the moment the resolved label differs from what's drawn.
          if (c.label !== slot.label) {
            slot.container.removeChild(slot.chip);
            slot.chip.destroy({ children: true });
            const { chip, text } = buildCursorChip(c.label, cursorColor(by));
            slot.container.addChild(chip);
            slot.chip = chip;
            slot.text = text;
            slot.label = c.label;
          }
        }
      }
      for (const [by, slot] of cursorSlots) {
        if (!incoming.has(by)) slot.alive = false;
      }
    },
    addPing(at, by) {
      const color = cursorColor(by);
      const container = new Container();
      container.x = at.x;
      container.y = at.y;
      container.scale.set(0.3);
      container.addChild(
        new Graphics().circle(0, 0, CELL * 0.95).stroke({ width: 4, color }),
        new Graphics().circle(0, 0, CELL * 0.55).stroke({ width: 3, color, alpha: 0.7 }),
        new Graphics().circle(0, 0, CELL * 0.16).fill(color),
      );
      vfxContainer.addChild(container);
      pingBursts.push({ container, startedAt: performance.now() });
    },
    spawnFloat(item) {
      if (item.tone === "caption") {
        // A dark plate with an amber rule under the caption text, centred over the token's top edge.
        const text = new Text({
          text: item.text,
          style: new TextStyle({ fill: FLOAT_COLORS.caption, fontFamily: "JetBrains Mono, monospace", fontWeight: "700", fontSize: 15, letterSpacing: 2 }),
        });
        text.anchor.set(0.5, 1);
        const padX = 10;
        const padY = 5;
        const w = text.width + padX * 2;
        const h = text.height + padY * 2;
        const plate = new Graphics().rect(-w / 2, -h, w, h).fill({ color: CAPTION_PLATE, alpha: 0.92 });
        const rule = new Graphics().rect(-w / 2, -2, w, 2).fill({ color: CAPTION_RULE });
        text.y = -padY;
        const caption = new Container();
        caption.addChild(plate, rule, text);
        caption.x = item.x;
        caption.y = item.y - 6;
        vfxContainer.addChild(caption);
        sharpenText(caption);
        floatEntries.push({ node: caption, startedAt: performance.now(), startY: caption.y, caption: true });
        return;
      }
      const node = new Text({
        text: item.text,
        style: new TextStyle({
          fill: FLOAT_COLORS[item.tone],
          fontFamily: "JetBrains Mono, monospace",
          fontWeight: "700",
          fontSize: 18,
          stroke: { color: "#0b0e13", width: 3 },
        }),
      });
      node.anchor.set(0.5, 1); // centred on x, bottom-anchored so it sits just above `item.y`
      node.x = item.x;
      node.y = item.y;
      vfxContainer.addChild(node);
      sharpenText(node);
      floatEntries.push({ node, startedAt: performance.now(), startY: item.y });
    },
    setFocus(ids) {
      focusIds = [...ids];
      if (focusIds.length === 0) focusRing.clear();
    },
    spawnProjectile(item) {
      if ("clear" in item) attackFx.clear();
      else attackFx.travel(item.attack);
    },
    spawnImpact(item) {
      if ("attack" in item) {
        attackFx.outcome(item.attack);
        return;
      }
      const container = new Container();
      container.x = item.x;
      container.y = item.y;
      container.scale.set(0.35);
      // outer shock ring → inner flare → hot core: a fireball for a ballistic hit, or the SAME
      // three-ring shape recolored hacker-green for a quick-tech flourish (`item.tech`) — no second
      // particle system, just a different palette at spawn time.
      const [ring, flare, core] = item.tech
        ? [TECH_BURST_RING, TECH_BURST_FLARE, TECH_BURST_CORE]
        : ["#ff7a1a", "#ffae3d", "#fff1c2"];
      container.addChild(
        new Graphics().circle(0, 0, CELL * 0.7).stroke({ width: 6, color: ring }),
        new Graphics().circle(0, 0, CELL * 0.45).fill({ color: flare, alpha: 0.55 }),
        new Graphics().circle(0, 0, CELL * 0.22).fill({ color: core }),
      );
      vfxContainer.addChild(container);
      impactEntries.push({ container, startedAt: performance.now() });
    },
    getMaxFPS() {
      return app.ticker.maxFPS;
    },
    setMaxFPS(fps) {
      const clamped = Number.isFinite(fps) && fps > 0 ? fps : 0;
      app.ticker.maxFPS = clamped;
      persistMaxFps(clamped);
    },
    getStats() {
      return { measuredFps, maxFPS: app.ticker.maxFPS, frameCount: totalFrames, rendererKind };
    },
    debugAttackOverlayCount() {
      return attackOverlayContainer.children.length;
    },
    debugAreaEffectCount() {
      return areaEffectFills.length;
    },
    debugDroneAuraCount() {
      return droneAuraCount;
    },
    debugMoveRangeCount() {
      return moveRangeContainer.children.length;
    },
    debugSensorRangeCount() {
      return sensorRangeContainer.children.length;
    },
    debugAttackFxLog() {
      return attackFx.log();
    },
    debugGhostPos(id) {
      const entry = ghostEntries.get(id);
      return entry ? { x: entry.x, y: entry.y } : undefined;
    },
    debugMovePathCount() {
      return movePathContainer.children.length;
    },
    debugMovePathBoostCells() {
      return movePathBoostCells;
    },
    debugPendingDeployCount() {
      return pendingDeployContainer.children.length;
    },
    debugCursorLabels() {
      return Array.from(cursorSlots.entries()).map(([by, slot]) => ({ by, label: slot.text.text }));
    },
    debugTokenBounds() {
      const bounds: Record<string, { x: number; y: number; w: number; h: number }> = {};
      for (const [id, entry] of tokenEntries) {
        const over = tokenOverflow(entry.side, CELL);
        bounds[id] = { x: entry.container.x - over.x, y: entry.container.y - over.y, w: entry.side + 2 * over.x, h: entry.side + 2 * over.y };
      }
      return bounds;
    },
    debugCellScreenPos(key) {
      // Parsed inline rather than through `parseTileKey`, which THROWS on a malformed key
      // (`parseSquareKey`, `packages/space/src/square.ts:45`). A debug accessor should hand back
      // `undefined` for junk rather than throw across the CDP boundary — where the error surfaces as
      // an opaque evaluate failure — and catching it here would mean a `try` block needing its own
      // entry in `scripts/try-allowlist.ts` for no benefit.
      const [xs, ys] = key.split(",");
      const x = Number(xs);
      const y = Number(ys);
      if (xs === undefined || ys === undefined || !Number.isFinite(x) || !Number.isFinite(y)) return undefined;
      // The cell's world-space CENTER, then through `world`'s own transform — so the returned point
      // already carries the live pan/zoom and needs no reconstruction on the caller's side.
      const p = world.toGlobal({ x: x * CELL + CELL / 2, y: y * CELL + CELL / 2 });
      return { x: p.x, y: p.y };
    },
    debugLabelBounds() {
      const bounds: Record<string, { x: number; y: number; w: number; h: number; text: string }> = {};
      for (const [id, entry] of labelEntries) {
        // A label whose pill isn't drawn reports NO rect — it occupies no pixels, so handing back its
        // anchor rect would let `frontend/e2e/label-layout.e2e.ts`'s "no label occludes a foreign
        // token" check fail on geometry nobody can see. `bg.visible` (not `where`) is the test on
        // purpose: it is the one fact that is true exactly when the pill is on screen, so a hidden
        // label that hover/selection has REVEALED correctly comes back with a rect. Read
        // `debugLabelStates()` for the `where`/`revealed` pair behind it.
        if (!entry.bg.visible) continue;
        // `container.x/y` IS the pill's world-space top-left corner (the bg is always drawn from
        // local `(0,0)` to `(w,h)` — see `LabelEntry`'s doc), so this is the real visual rect the
        // player sees, not a container origin or an anchor-relative point. `w`/`h` is the same
        // measured pill size the solver placed against, and `text.text` is the LIVE rendered
        // (truncated) text, mirroring `debugCursorLabels`' "read the pixels" pattern.
        bounds[id] = { x: entry.container.x, y: entry.container.y, w: entry.w, h: entry.h, text: entry.text.text };
      }
      return bounds;
    },
    debugLabelStates() {
      const states: Record<string, { where: PlacedWhere; drawn: boolean; revealed: boolean }> = {};
      for (const [id, entry] of labelEntries) {
        states[id] = { where: entry.where, drawn: entry.bg.visible, revealed: entry.container.parent === labelRevealContainer };
      }
      return states;
    },
    setTargetRings(ids, opts) {
      for (const ring of targetRings.values()) ring.destroy();
      targetRings.clear();
      targetRingContainer.removeChildren();
      targetRingPulse = opts.pulse ? { periodMs: TARGET_RING_PERIOD_MS[opts.phase] } : null;
      targetRingPhase = opts.phase;
      const now = performance.now();
      const prevBornAt = targetRingBornAt;
      targetRingBornAt = new Map();
      for (const id of ids) {
        const prev = prevBornAt.get(id);
        targetRingBornAt.set(id, prev !== undefined ? prev : opts.spin ? now : null);
      }
      const color = `rgb(${TARGETING_RGB[opts.kind]})`;
      for (const id of ids) {
        const entry = tokenEntries.get(id);
        // A hex like the token, so it never reads as a peak mark.
        const hex = hexPoints(0, 0, markRingRadius(entry?.side ?? CELL, CELL));
        const ring = new Graphics();
        ring.poly(hex, true).stroke({ width: 7, color, alpha: 0.35 });
        strokeTwice(ring, (g) => g.poly(hex, true), { under: { width: 3 + BAND_UNDER.grow, color: BAND_UNDER.color, alpha: BAND_UNDER.alpha }, top: { width: 3, color } });
        targetRings.set(id, ring);
        targetRingContainer.addChild(ring);
      }
    },
    debugTargetRingIds() {
      return [...targetRings.keys()];
    },
    debugTargetRingSpin() {
      return { phase: targetRingPhase, bornAt: Object.fromEntries(targetRingBornAt) };
    },
    debugAnnotationIds() {
      return drawnAnnotationIds.slice();
    },
    setTargetBadges(badges, opts) {
      expandedBadgeId = opts.expandedId;
      const key = [...badges].map(([id, b]) => `${id}=${b.kind}|${targetBadgeText(b)}`).sort().join(";");
      if (key === badgeKey) {
        syncBadgeVisibility();
        return;
      }
      badgeKey = key;
      for (const [id, b] of [...badgeEntries]) {
        if (badges.has(id)) continue;
        b.container.destroy({ children: true });
        badgeEntries.delete(id);
      }
      for (const [id, spec] of badges) {
        let b = badgeEntries.get(id);
        if (!b) {
          const container = new Container();
          const bg = new Graphics();
          const text = new Text({ text: "", style: new TextStyle({ fill: TOKEN_INK, fontFamily: "JetBrains Mono, monospace", fontSize: TARGET_BADGE_FONT, fontWeight: "600" }) });
          text.x = TARGET_BADGE_PAD_X;
          text.y = TARGET_BADGE_PAD_Y;
          container.addChild(bg, text);
          targetBadgeContainer.addChild(container);
          b = { container, bg, text, spec, w: 0, h: 0, dx: 0, dy: 0, where: "hidden", slot: undefined };
          badgeEntries.set(id, b);
        }
        b.spec = spec;
        b.text.text = targetBadgeText(spec);
        b.w = b.text.width + TARGET_BADGE_PAD_X * 2;
        b.h = b.text.height + TARGET_BADGE_PAD_Y * 2;
        b.bg
          .clear()
          .roundRect(0, 0, b.w, b.h, TOKEN_LABEL_RADIUS)
          .fill({ color: TOKEN_LABEL_BG, alpha: 0.88 })
          .stroke({ width: 1, color: `rgb(${TARGETING_RGB[spec.kind]})` });
      }
      solveBadges();
      syncBadgeVisibility();
      sharpenText(targetBadgeContainer);
    },
    debugTargetBadges() {
      const out: Record<string, { text: string; x: number; y: number; w: number; h: number; where: PlacedWhere; visible: boolean }> = {};
      for (const [id, b] of badgeEntries) {
        const t = tokenEntries.get(id);
        const rest = t ? restRect(t) : { x: 0, y: 0 };
        out[id] = { text: b.text.text, x: rest.x + b.dx, y: rest.y + b.dy, w: b.w, h: b.h, where: b.where, visible: b.container.visible };
      }
      return out;
    },
    debugMineTokenIds() {
      return [...tokenEntries].filter(([, e]) => e.mine).map(([id]) => id);
    },
    debugTokenArt() {
      return Object.fromEntries([...tokenEntries].map(([id, e]) => [id, { ...e.art }]));
    },
    debugTokenChrome() {
      return Object.fromEntries([...tokenEntries].flatMap(([id, e]) => (e.chrome ? [[id, e.chrome]] : [])));
    },
    debugActiveBeacon() {
      return beacon && beaconRing.visible ? { ...beacon } : null;
    },
    debugChromeRowBounds() {
      const bounds: Record<string, { x: number; y: number; w: number; h: number }> = {};
      for (const [id, e] of tokenEntries) {
        const row = e.chrome ? tokenChromeObstacle(e.chrome, { side: e.side, cell: CELL }) : null;
        if (row) bounds[id] = { ...row, x: row.x + e.container.x, y: row.y + e.container.y };
      }
      return bounds;
    },
    destroy() {
      dprQuery?.removeEventListener("change", onDprChange);
      viewSettled.dispose();
      terrainLayer.destroy();
      assetLayer.destroy();
      app.destroy(true, { children: true, texture: true });
    },
  };
}
